import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import RoleSwitcher from '../components/RoleSwitcher';
import ConfirmModal from '../components/ConfirmModal';
import { Truck, Clock, CheckCircle, XCircle, MapPin, DollarSign, Package, Plus, Trash2, Calendar, Copy, Power, Edit2 } from 'lucide-react';
import { Textarea } from '../components/ui/textarea';
import { formatDateAU, formatDateShortAU } from '../utils/dateFormat';
import { CarrierNegotiationsPanel } from '../components/QuoteNegotiation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TRAILER_LABELS = {
  flat_top: 'Flat Top', drop_deck: 'Drop Deck', low_loader: 'Low Loader',
  tautliner: 'Tautliner', refrigerated: 'Refrigerated', tanker: 'Tanker',
  side_loader: 'Side Loader', skel_container: 'Skel/Container', float: 'Float',
  extendable: 'Extendable', b_double: 'B-Double', road_train: 'Road Train',
  tilt_tray: 'Tilt Tray', car_carrier: 'Car Carrier', livestock: 'Livestock',
  bulk_tipper: 'Bulk Tipper', walking_floor: 'Walking Floor', other: 'Other'
};
const STATES = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];

const AvailabilityTab = ({ carrierEmail }) => {
  const [listings, setListings] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    fleet_number: '',
    location_suburb: '', location_state: 'NSW', trailer_types: [],
    max_weight_kg: '', max_dimensions: '', special_capabilities: '',
    available_from: '', available_until: '', preferred_regions: [],
    preferred_routes: '', notes: ''
  });

  const [showPaywall, setShowPaywall] = useState(false);
  const [pendingListing, setPendingListing] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null); // 'active' | 'adhoc' | other
  const [listingFilter, setListingFilter] = useState('all'); // all | active | pending
  const [weeklySub, setWeeklySub] = useState(null); // { active, truck_count, overage_qty, estimated_weekly_cost }
  const [subscribing, setSubscribing] = useState(false);
  const [showCancelWeekly, setShowCancelWeekly] = useState(false);

  useEffect(() => { fetchData(); }, [carrierEmail]);

  useEffect(() => {
    // Detect subscription state once for paywall gating
    let cancelled = false;
    axios.get(`${API}/users/${encodeURIComponent(carrierEmail)}/subscription`)
      .then(r => { if (!cancelled) setSubscriptionStatus(r.data?.subscription_status || 'unknown'); })
      .catch(() => {});
    axios.get(`${API}/transporter-listings/weekly-subscribe/${encodeURIComponent(carrierEmail)}`)
      .then(r => { if (!cancelled) setWeeklySub(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [carrierEmail]);

  const isPlatformSubscriber = subscriptionStatus === 'active';
  const hasWeeklyTransporterSub = !!weeklySub?.active;
  const isAdhoc = !isPlatformSubscriber && !hasWeeklyTransporterSub;

  const fetchData = async () => {
    setLoading(true);
    try {
      const [listRes, reqRes] = await Promise.all([
        axios.get(`${API}/transporter-listings/my/${carrierEmail}`),
        axios.get(`${API}/transporter-requests/carrier/${carrierEmail}`)
      ]);
      setListings(listRes.data);
      setRequests(reqRes.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.location_suburb || !form.location_state || form.trailer_types.length === 0) {
      alert('Please fill in location and at least one trailer type'); return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/transporter-listings`, {
        carrier_email: carrierEmail, ...form,
        max_weight_kg: form.max_weight_kg ? parseInt(form.max_weight_kg) : null
      });
      const created = res.data;
      // If backend flagged the listing as pending payment (adhoc), open Stripe checkout
      if (created?.payment_status === 'pending') {
        try {
          const chk = await axios.post(`${API}/transporter-listings/${created.id}/checkout`, {
            carrier_email: carrierEmail,
            origin: window.location.origin
          });
          if (chk.data?.checkout_url) {
            window.location.href = chk.data.checkout_url;
            return;
          }
        } catch (chkErr) {
          alert('Listing saved as pending. Stripe checkout could not be opened — please try again from your listing.');
        }
      } else {
        alert('Listing posted! It is now visible to shippers on the Transporters page.');
      }
      setShowForm(false);
      setForm({ fleet_number: '', location_suburb: '', location_state: 'NSW', trailer_types: [], max_weight_kg: '', max_dimensions: '', special_capabilities: '', available_from: '', available_until: '', preferred_regions: [], preferred_routes: '', notes: '' });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to post listing');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this listing?')) return;
    try {
      await axios.delete(`${API}/transporter-listings/${id}?carrier_email=${carrierEmail}`);
      fetchData();
    } catch { alert('Failed to delete'); }
  };

  const handleDuplicate = (listing) => {
    // Pre-fill the form with the listing's values, clear fleet_number so the carrier enters a new one
    setForm({
      fleet_number: '',
      location_suburb: listing.location_suburb || '',
      location_state: listing.location_state || 'NSW',
      trailer_types: listing.trailer_types || [],
      max_weight_kg: listing.max_weight_kg ? String(listing.max_weight_kg) : '',
      max_dimensions: listing.max_dimensions || '',
      special_capabilities: listing.special_capabilities || '',
      available_from: listing.available_from || '',
      available_until: listing.available_until || '',
      preferred_regions: listing.preferred_regions || [],
      preferred_routes: listing.preferred_routes || '',
      notes: listing.notes || '',
    });
    setShowForm(true);
    setTimeout(() => window.scrollTo({ top: 300, behavior: 'smooth' }), 100);
  };

  const handleBulkAvailability = async (isAvailable) => {
    try {
      const res = await axios.post(`${API}/transporter-listings/bulk-availability`, {
        carrier_email: carrierEmail,
        is_available_to_load: isAvailable,
      });
      const n = res.data?.updated_count || 0;
      // Refresh listings
      fetchData();
      if (n === 0) return;
    } catch (e) {
      alert(e.response?.data?.detail || 'Bulk update failed');
    }
  };

  const handlePayPending = async (listing) => {
    try {
      if (listing.stripe_checkout_url) {
        window.location.href = listing.stripe_checkout_url;
        return;
      }
      const res = await axios.post(`${API}/transporter-listings/${listing.id}/checkout`, {
        carrier_email: carrierEmail,
        origin: window.location.origin
      });
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        alert('Could not open Stripe checkout. Please try again.');
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to open checkout');
    }
  };

  const handleStartWeeklySub = async () => {
    setSubscribing(true);
    try {
      const res = await axios.post(`${API}/transporter-listings/weekly-subscribe`, {
        carrier_email: carrierEmail,
        origin: window.location.origin,
      });
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }
      alert('Could not open Stripe checkout. Please try again.');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to start subscription');
    } finally {
      setSubscribing(false);
    }
  };

  const handleCancelWeeklySub = async () => {
    try {
      await axios.post(`${API}/transporter-listings/weekly-subscribe/cancel`, {
        carrier_email: carrierEmail,
      });
      const r = await axios.get(`${API}/transporter-listings/weekly-subscribe/${encodeURIComponent(carrierEmail)}`);
      setWeeklySub(r.data);
      setShowCancelWeekly(false);
      alert('Weekly subscription will end at the current period.');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to cancel subscription');
    }
  };

  // Handle Stripe return: confirm weekly subscription activation via query param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (params.get('weekly') === '1' && sessionId) {
      axios.get(`${API}/transporter-listings/weekly-subscribe/status/${sessionId}`)
        .then(async () => {
          const r = await axios.get(`${API}/transporter-listings/weekly-subscribe/${encodeURIComponent(carrierEmail)}`);
          setWeeklySub(r.data);
          await fetchData();
          // Clean URL
          params.delete('weekly'); params.delete('session_id');
          const clean = params.toString();
          window.history.replaceState({}, '', window.location.pathname + (clean ? `?${clean}` : ''));
        })
        .catch(() => {});
    }
  }, [carrierEmail]);

  const handleQuoteRequest = async (requestId) => {
    const amount = prompt('Enter your quote amount ($):');
    if (!amount || isNaN(amount)) return;
    const msg = prompt('Optional message to shipper:') || '';
    try {
      await axios.post(`${API}/transporter-requests/${requestId}/quote`, {
        carrier_email: carrierEmail, amount: parseFloat(amount), message: msg
      });
      alert('Quote submitted! Admin will review before the shipper sees it.');
      fetchData();
    } catch (error) { alert(error.response?.data?.detail || 'Failed to submit quote'); }
  };

  const toggleTrailer = (type) => {
    setForm(prev => ({
      ...prev, trailer_types: prev.trailer_types.includes(type)
        ? prev.trailer_types.filter(t => t !== type)
        : [...prev.trailer_types, type]
    }));
  };
  const toggleRegion = (state) => {
    setForm(prev => ({
      ...prev, preferred_regions: prev.preferred_regions.includes(state)
        ? prev.preferred_regions.filter(s => s !== state)
        : [...prev.preferred_regions, state]
    }));
  };

  if (loading) return <Card className="p-8 text-center text-gray-500">Loading...</Card>;

  return (
    <div className="space-y-6">
      {/* Plan status banner */}
      {isPlatformSubscriber && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 flex items-center gap-2" data-testid="plan-status-subscriber">
          <CheckCircle className="w-4 h-4" /> Availability listings are <strong>free</strong> on your active subscription.
        </div>
      )}
      {!isPlatformSubscriber && hasWeeklyTransporterSub && weeklySub && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900 flex items-center justify-between flex-wrap gap-2" data-testid="plan-status-weekly">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            <span>Weekly transporter plan active — <strong>{weeklySub.truck_count} truck{weeklySub.truck_count === 1 ? '' : 's'}</strong>, <strong>${weeklySub.estimated_weekly_cost}/wk</strong>{weeklySub.overage_qty > 0 ? ` (includes ${weeklySub.overage_qty} × $0.50 overage)` : ''}.</span>
          </div>
          <Button variant="outline" size="sm" onClick={() => setShowCancelWeekly(true)} data-testid="cancel-weekly-btn">
            Cancel
          </Button>
        </div>
      )}
      {!isPlatformSubscriber && !hasWeeklyTransporterSub && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="plan-status-adhoc">
          Ad-hoc pricing: <strong>$4.99</strong> per truck (7-day pass) or <strong>$1.99/wk</strong> subscription (up to 10 trucks; +$0.50/wk each extra).
        </div>
      )}

      {/* Cancel-weekly confirm modal */}
      {showCancelWeekly && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" onClick={() => setShowCancelWeekly(false)} data-testid="cancel-weekly-backdrop">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-[#1e3a5f] mb-2">Cancel weekly subscription?</h3>
            <p className="text-sm text-gray-700 mb-4">Your subscription will end at the current billing period. Existing listings will revert to <strong>pending payment</strong> once the period lapses.</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowCancelWeekly(false)} className="flex-1" data-testid="cancel-weekly-no">Keep it</Button>
              <Button onClick={handleCancelWeeklySub} className="flex-1 bg-red-600 hover:bg-red-700 text-white" data-testid="cancel-weekly-yes">Yes, cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Post New Listing */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-[#1e3a5f]">Your Transporter Listings</h2>
          {!showForm && (
            <Button
              onClick={() => {
                if (isAdhoc) { setShowPaywall(true); } else { setShowForm(true); }
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="post-listing-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Post Availability
            </Button>
          )}
        </div>

        {/* Paywall modal for adhoc carriers */}
        {showPaywall && (
          <div
            className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4"
            onClick={() => setShowPaywall(false)}
            data-testid="availability-paywall-backdrop"
          >
            <div
              className="bg-white rounded-2xl p-6 max-w-lg w-full shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-[#1e3a5f] mb-1">Availability listing — paid feature</h3>
              <p className="text-sm text-gray-700 mb-4">
                <strong>Free for paid subscribers.</strong> As an ad-hoc member, choose an option below.
              </p>

              {/* Option A — one-off */}
              <div className="border rounded-lg p-4 mb-3">
                <div className="flex items-baseline justify-between mb-1">
                  <h4 className="font-semibold text-[#1e3a5f]">One-off listing</h4>
                  <span className="text-lg font-bold text-red-600">$4.99</span>
                </div>
                <p className="text-xs text-gray-600 mb-3">One truck, 1-week pass. Auto-expires after 7 days.</p>
                <Button
                  onClick={() => { setShowPaywall(false); setShowForm(true); }}
                  className="w-full bg-red-600 hover:bg-red-700 text-white"
                  data-testid="paywall-oneoff-btn"
                >
                  Continue with one-off ($4.99)
                </Button>
              </div>

              {/* Option B — weekly subscription */}
              <div className="border-2 border-blue-500 rounded-lg p-4 mb-3 bg-blue-50/40 relative">
                <div className="absolute -top-2 left-3 bg-blue-600 text-white text-[10px] px-2 py-0.5 rounded">BEST VALUE</div>
                <div className="flex items-baseline justify-between mb-1">
                  <h4 className="font-semibold text-[#1e3a5f]">Weekly subscription</h4>
                  <span className="text-lg font-bold text-blue-700">$1.99/wk</span>
                </div>
                <p className="text-xs text-gray-600 mb-1">Up to 10 trucks. <strong>+$0.50/wk</strong> for each additional truck.</p>
                <p className="text-xs text-gray-500 mb-3">Cancel anytime. Renews weekly.</p>
                <Button
                  onClick={handleStartWeeklySub}
                  disabled={subscribing}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="paywall-weekly-btn"
                >
                  {subscribing ? 'Opening checkout…' : 'Start weekly subscription ($1.99/wk)'}
                </Button>
              </div>

              <div className="flex gap-2 mt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowPaywall(false)}
                  className="flex-1"
                  data-testid="availability-paywall-cancel"
                >
                  Not now
                </Button>
              </div>
            </div>
          </div>
        )}

        {showForm && (
          <form onSubmit={handleSave} className="border-t pt-4 space-y-4">
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <Label>Fleet # <span className="text-xs text-gray-400">(optional, max 6 chars)</span></Label>
                <Input
                  value={form.fleet_number}
                  onChange={e => setForm(p => ({...p, fleet_number: e.target.value.toUpperCase().slice(0, 6)}))}
                  placeholder="e.g. T01"
                  maxLength={6}
                  data-testid="listing-fleet-number-input"
                />
              </div>
              <div><Label>Suburb *</Label><Input value={form.location_suburb} onChange={e => setForm(p => ({...p, location_suburb: e.target.value}))} placeholder="e.g. Dublin" required /></div>
              <div><Label>State *</Label>
                <select value={form.location_state} onChange={e => setForm(p => ({...p, location_state: e.target.value}))} className="w-full h-10 border rounded-md px-3 text-sm">
                  {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            <div>
              <Label>Trailer Types * (select all that apply)</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {Object.entries(TRAILER_LABELS).map(([k, v]) => (
                  <button key={k} type="button" onClick={() => toggleTrailer(k)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${form.trailer_types.includes(k) ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
                    {v}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Max Weight (kg)</Label><Input type="number" value={form.max_weight_kg} onChange={e => setForm(p => ({...p, max_weight_kg: e.target.value}))} placeholder="e.g. 25000" /></div>
              <div><Label>Max Dimensions</Label><Input value={form.max_dimensions} onChange={e => setForm(p => ({...p, max_dimensions: e.target.value}))} placeholder="e.g. 13.6m x 2.5m x 2.7m" /></div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-[#1e3a5f] mb-2 flex items-center gap-1.5">
                <Calendar className="w-4 h-4" /> Dates at This Location
              </h4>
              <p className="text-xs text-gray-500 mb-3">Let shippers know when you'll be at this location and available for work.</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Available From *</Label><Input type="date" value={form.available_from} onChange={e => setForm(p => ({...p, available_from: e.target.value}))} required /></div>
                <div><Label>Available Until</Label><Input type="date" value={form.available_until} onChange={e => setForm(p => ({...p, available_until: e.target.value}))} /><p className="text-xs text-gray-400 mt-0.5">Leave blank if ongoing / flexible</p></div>
              </div>
            </div>

            <div>
              <Label>Preferred Regions</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {STATES.map(s => (
                  <button key={s} type="button" onClick={() => toggleRegion(s)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${form.preferred_regions.includes(s) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div><Label>Preferred Routes</Label><Input value={form.preferred_routes} onChange={e => setForm(p => ({...p, preferred_routes: e.target.value}))} placeholder="e.g. Adelaide to Melbourne, East Coast runs" /></div>
            <div><Label>Special Capabilities</Label><Input value={form.special_capabilities} onChange={e => setForm(p => ({...p, special_capabilities: e.target.value}))} placeholder="e.g. OSOM, Dangerous goods, Refrigerated" /></div>
            <div><Label>Notes</Label><Textarea value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} rows={2} placeholder="Anything else shippers should know..." /></div>

            <div className="flex gap-3">
              <Button type="submit" disabled={saving} className="bg-green-600 hover:bg-green-700 text-white">{saving ? 'Posting...' : 'Post Listing'}</Button>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </form>
        )}

        {/* Existing Listings */}
        {listings.length >= 2 && !showForm && (() => {
          const activeListings = listings.filter(l => l.status !== 'expired');
          const allAvailable = activeListings.length > 0 && activeListings.every(l => l.is_available_to_load !== false);
          return (
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2 mt-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
              <div className="text-sm text-slate-700 flex items-center gap-2">
                <Power className="w-4 h-4 text-slate-500" />
                <span><strong>Bulk availability</strong> — flip every truck in one click</span>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={allAvailable ? 'default' : 'outline'}
                  className={allAvailable ? 'bg-green-600 hover:bg-green-700 text-white' : 'text-green-700 border-green-500 hover:bg-green-50'}
                  onClick={() => handleBulkAvailability(true)}
                  data-testid="bulk-set-available"
                >
                  All available
                </Button>
                <Button
                  size="sm"
                  variant={!allAvailable ? 'default' : 'outline'}
                  className={!allAvailable ? 'bg-gray-600 hover:bg-gray-700 text-white' : 'text-gray-700 border-gray-400 hover:bg-gray-50'}
                  onClick={() => handleBulkAvailability(false)}
                  data-testid="bulk-set-unavailable"
                >
                  All unavailable
                </Button>
              </div>
            </div>
          );
        })()}

        {listings.length > 0 && !showForm && (
          <div className="flex gap-2 flex-wrap items-center mb-2 mt-3">
            <span className="text-xs text-gray-500 font-medium">Filter:</span>
            {[
              { v: 'all', label: `All (${listings.length})` },
              { v: 'active', label: `Active (${listings.filter(l => (l.payment_status !== 'pending' && l.status !== 'pending_payment' && l.status !== 'expired')).length})` },
              { v: 'pending', label: `Pending payment (${listings.filter(l => l.payment_status === 'pending' || l.status === 'pending_payment').length})` }
            ].map(opt => (
              <Button
                key={opt.v}
                size="sm"
                variant={listingFilter === opt.v ? 'default' : 'outline'}
                className={listingFilter === opt.v ? (opt.v === 'pending' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-[#1e3a5f]') : ''}
                onClick={() => setListingFilter(opt.v)}
                data-testid={`listings-filter-${opt.v}`}
              >
                {opt.label}
              </Button>
            ))}
          </div>
        )}
        {listings.length === 0 && !showForm && (
          <p className="text-gray-500 text-center py-6">No active listings. Post your availability to let shippers find you.</p>
        )}
        {listings.filter(l => {
          if (listingFilter === 'all') return true;
          const isPending = l.status === 'pending_payment' || l.payment_status === 'pending';
          if (listingFilter === 'pending') return isPending;
          if (listingFilter === 'active') return !isPending && l.status !== 'expired';
          return true;
        }).map(l => {
          const isPending = l.status === 'pending_payment' || l.payment_status === 'pending';
          const editsLeft = isPending ? Math.max(0, 3 - (l.edit_count || 0)) : null;
          return (
          <Card key={l.id} className={`p-4 mt-3 border-l-4 ${l.status === 'expired' ? 'border-l-gray-300 opacity-60' : isPending ? 'border-l-amber-500 bg-amber-50/40' : 'border-l-blue-500'}`}>
            <div className="flex justify-between items-start gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  {l.fleet_number && (
                    <span
                      className="text-xs font-bold px-2 py-0.5 rounded bg-[#1e3a5f] text-white font-mono"
                      data-testid={`fleet-badge-${l.id}`}
                    >
                      #{l.fleet_number}
                    </span>
                  )}
                  <p className="font-semibold text-[#1e3a5f]">{l.location_suburb}, {l.location_state}</p>
                  {l.status === 'expired' && <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">Expired</span>}
                  {isPending && (
                    <span className="text-xs bg-amber-200 text-amber-900 px-2 py-0.5 rounded font-semibold" data-testid={`pending-badge-${l.id}`}>
                      Pending payment ($4.99 / 7-day pass) — hidden from public
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-1 mt-1">{(l.trailer_types || []).map(t => <span key={t} className="text-xs bg-gray-100 px-2 py-0.5 rounded">{TRAILER_LABELS[t] || t}</span>)}</div>
                {(l.available_from || l.available_until) && (
                  <p className={`text-xs mt-1.5 flex items-center gap-1 ${l.status === 'expired' ? 'text-gray-400' : 'text-blue-700'}`}>
                    <Calendar className="w-3 h-3" />
                    {l.available_from ? new Date(l.available_from).toLocaleDateString('en-AU') : 'Now'}
                    {' — '}
                    {l.available_until ? new Date(l.available_until).toLocaleDateString('en-AU') : 'Ongoing'}
                  </p>
                )}
                {l.preferred_routes && <p className="text-xs text-gray-500 mt-1">Routes: {l.preferred_routes}</p>}
                {isPending && (
                  <p className="text-xs text-amber-800 mt-1.5">
                    Edits used: {l.edit_count || 0}/3 ({editsLeft} left).
                    Complete payment to publish.
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-1.5 items-end">
                {isPending && (
                  <Button
                    size="sm"
                    className="bg-red-600 hover:bg-red-700 text-white"
                    onClick={() => handlePayPending(l)}
                    data-testid={`pay-pending-${l.id}`}
                  >
                    Pay $4.99
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDuplicate(l)}
                  className="text-blue-600 hover:text-blue-800 border-blue-300"
                  data-testid={`duplicate-listing-${l.id}`}
                  title="Duplicate this listing"
                >
                  <Copy className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleDelete(l.id)} className="text-red-500 hover:text-red-700" data-testid={`delete-listing-${l.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Quick toggles: available to load + always visible */}
            <div className="flex flex-wrap gap-3 pt-3 mt-3 border-t border-dashed">
              <button
                type="button"
                onClick={async () => {
                  try {
                    await axios.patch(`${API}/transporter-listings/${l.id}/status`, {
                      carrier_email: carrierEmail,
                      is_available_to_load: !(l.is_available_to_load !== false),
                    });
                    fetchData();
                  } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
                }}
                className={`flex items-center gap-2 text-xs px-2 py-1 rounded transition-colors ${
                  l.is_available_to_load !== false ? 'bg-green-50 text-green-800 hover:bg-green-100' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid={`toggle-load-${l.id}`}
              >
                <span className={`w-2 h-2 rounded-full ${l.is_available_to_load !== false ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                {l.is_available_to_load !== false ? 'Available to load' : 'Not available'}
              </button>
              <button
                type="button"
                onClick={async () => {
                  try {
                    await axios.patch(`${API}/transporter-listings/${l.id}/status`, {
                      carrier_email: carrierEmail,
                      always_visible: !l.always_visible,
                    });
                    fetchData();
                  } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
                }}
                className={`flex items-center gap-2 text-xs px-2 py-1 rounded transition-colors ${
                  l.always_visible ? 'bg-blue-50 text-blue-800 hover:bg-blue-100' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid={`toggle-visible-${l.id}`}
              >
                <span className={`w-2 h-2 rounded-full ${l.always_visible ? 'bg-blue-500' : 'bg-gray-400'}`}></span>
                {l.always_visible ? 'Always visible on map' : 'Hidden after expiry'}
              </button>
            </div>
          </Card>
          );
        })}
      </Card>

      {/* Transport Requests */}
      <Card className="p-6">
        <h2 className="text-xl font-bold text-[#1e3a5f] mb-4">Transport Requests</h2>
        {requests.length === 0 ? (
          <p className="text-gray-500 text-center py-6">No transport requests yet. When a shipper requests a quote from your listing, it will appear here.</p>
        ) : (
          <div className="space-y-3">
            {requests.map(r => (
              <Card key={r.id} className={`p-4 border-l-4 ${r.status === 'pending' ? 'border-l-yellow-500' : r.status === 'quoted' ? 'border-l-blue-500' : r.status === 'approved' ? 'border-l-green-500' : 'border-l-gray-300'}`}>
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-semibold text-[#1e3a5f]">{r.pickup_location} → {r.dropoff_location}</p>
                    <p className="text-sm text-gray-600">Cargo: {r.cargo_type}</p>
                    {r.weight && <p className="text-xs text-gray-500">Weight: {r.weight}</p>}
                    {r.message && <p className="text-xs text-gray-500 italic mt-1">"{r.message}"</p>}
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                    r.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    r.status === 'quoted' ? 'bg-blue-100 text-blue-800' :
                    r.status === 'approved' ? 'bg-green-100 text-green-800' :
                    r.status === 'accepted' ? 'bg-green-100 text-green-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>{r.status.toUpperCase()}</span>
                </div>
                {r.status === 'pending' && (
                  <Button size="sm" onClick={() => handleQuoteRequest(r.id)} className="bg-[#1e3a5f] hover:bg-[#15304f] text-white mt-2" data-testid={`quote-request-${r.id}`}>
                    <DollarSign className="w-3.5 h-3.5 mr-1" /> Submit Price
                  </Button>
                )}
                {r.quoted_amount && <p className="text-sm text-green-700 font-medium mt-2">Your quote: ${r.quoted_amount.toLocaleString()}</p>}
                {r.payment_completed && (
                  <div className="mt-2 p-2 bg-green-50 rounded text-sm text-green-800">
                    <strong>Paid!</strong> Shipper: {r.shipper_name} — {r.shipper_email}
                    {r.shipper_phone && <> — {r.shipper_phone}</>}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

const CarrierDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [carrierEmail, setCarrierEmail] = useState('');
  const [quotes, setQuotes] = useState([]);
  const [loads, setLoads] = useState([]);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'quotes');
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [showAddDocumentForm, setShowAddDocumentForm] = useState(false);
  const [showAddEquipmentForm, setShowAddEquipmentForm] = useState(false);
  const [showDeleteEquipModal, setShowDeleteEquipModal] = useState(false);
  const [showDeleteDocModal, setShowDeleteDocModal] = useState(false);
  const [carrierProfile, setCarrierProfile] = useState(null);
  const [equipToDelete, setEquipToDelete] = useState(null);
  const [docToDelete, setDocToDelete] = useState(null);
  const [newDocType, setNewDocType] = useState('');
  const [selectedEquipmentType, setSelectedEquipmentType] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [applyingPromo, setApplyingPromo] = useState(false);
  const [adhocPromoCode, setAdhocPromoCode] = useState('');
  const [processingAdhocPayment, setProcessingAdhocPayment] = useState(null);
  
  // Quote rescind state
  const [showRescindModal, setShowRescindModal] = useState(false);
  const [quoteToRescind, setQuoteToRescind] = useState(null);
  const [rescindReason, setRescindReason] = useState('');
  const [rescindingQuote, setRescindingQuote] = useState(false);
  const [historyLoads, setHistoryLoads] = useState([]);

  // Trailer-hire listings state
  const [hireListings, setHireListings] = useState([]);
  const [loadingHireListings, setLoadingHireListings] = useState(false);
  const [hireDeleteTarget, setHireDeleteTarget] = useState(null);
  const [hireDeleting, setHireDeleting] = useState(false);
  const [hireEditTarget, setHireEditTarget] = useState(null);
  const [hireEditForm, setHireEditForm] = useState({
    base_location: '',
    indicative_daily_rate: '',
    indicative_weekly_rate: '',
    indicative_monthly_rate: '',
    notes: '',
    status: 'active',
  });
  const [hireEditSaving, setHireEditSaving] = useState(false);

  // Initialize email from URL or localStorage with retry mechanism
  useEffect(() => {
    console.log('=== CARRIER DASHBOARD INIT ===');
    console.log('searchParams:', searchParams.toString());
    const emailFromUrl = searchParams.get('email');
    console.log('emailFromUrl:', emailFromUrl);
    
    if (emailFromUrl) {
      // URL parameter takes priority
      console.log('Setting email from URL:', emailFromUrl);
      setCarrierEmail(emailFromUrl);
      setInitialLoading(false);
    } else {
      console.log('No URL email, trying localStorage...');
      // Try localStorage with retry mechanism to handle race condition
      const tryGetEmail = (attempts = 0) => {
        const emailFromStorage = localStorage.getItem('user_email');
        console.log(`Attempt ${attempts + 1}: localStorage email =`, emailFromStorage);
        
        if (emailFromStorage) {
          console.log('Setting email from localStorage:', emailFromStorage);
          setCarrierEmail(emailFromStorage);
          setInitialLoading(false);
        } else if (attempts < 5) {
          // Retry after short delay (50ms intervals, max 250ms total)
          setTimeout(() => tryGetEmail(attempts + 1), 50);
        } else {
          // No email found after retries - show authentication required
          console.log('❌ No email found after all retries');
          setInitialLoading(false);
        }
      };
      
      tryGetEmail();
    }
  }, [searchParams]);

  useEffect(() => {
    if (carrierEmail) {
      fetchCarrierData();
      fetchHireListings();
      
      // Check for adhoc payment result
      const adhocPayment = searchParams.get('adhoc_payment');
      const loadId = searchParams.get('load_id');
      
      if (adhocPayment === 'success' && loadId) {
        verifyAdhocPayment(loadId);
      }
    }
  }, [carrierEmail]);

  const fetchHireListings = async () => {
    if (!carrierEmail) return;
    try {
      setLoadingHireListings(true);
      const res = await axios.get(`${API}/trailer-hire/my-listings/${encodeURIComponent(carrierEmail)}`);
      // Only show listings that aren't removed
      const items = (Array.isArray(res.data) ? res.data : []).filter((l) => l.status !== 'removed');
      setHireListings(items);
    } catch (e) {
      console.error('Failed to load hire listings', e);
    } finally {
      setLoadingHireListings(false);
    }
  };

  const openHireEdit = (listing) => {
    setHireEditTarget(listing);
    setHireEditForm({
      base_location: listing.base_location || '',
      indicative_daily_rate: listing.indicative_daily_rate ?? '',
      indicative_weekly_rate: listing.indicative_weekly_rate ?? '',
      indicative_monthly_rate: listing.indicative_monthly_rate ?? '',
      notes: listing.notes || '',
      status: listing.status || 'active',
    });
  };

  const saveHireEdit = async () => {
    if (!hireEditTarget) return;
    try {
      setHireEditSaving(true);
      const payload = { ...hireEditForm, operator_email: carrierEmail };
      // Rates are stored as strings per the backend model — keep user input as-is (trim).
      ['indicative_daily_rate', 'indicative_weekly_rate', 'indicative_monthly_rate'].forEach((k) => {
        if (payload[k] == null) {
          payload[k] = null;
        } else {
          const s = String(payload[k]).trim();
          payload[k] = s === '' ? null : s;
        }
      });
      await axios.put(`${API}/trailer-hire/listings/${hireEditTarget.id}`, payload);
      alert('Listing updated');
      setHireEditTarget(null);
      fetchHireListings();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Update failed';
      alert(msg);
    } finally {
      setHireEditSaving(false);
    }
  };

  const confirmHireDelete = async () => {
    if (!hireDeleteTarget) return;
    try {
      setHireDeleting(true);
      await axios.delete(`${API}/trailer-hire/listings/${hireDeleteTarget.id}`, {
        params: { operator_email: carrierEmail },
      });
      alert('Listing removed');
      setHireDeleteTarget(null);
      fetchHireListings();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Delete failed';
      alert(msg);
    } finally {
      setHireDeleting(false);
    }
  };

  const verifyAdhocPayment = async (loadId) => {
    try {
      const response = await axios.post(`${API}/carrier/verify-adhoc-payment/${loadId}`);
      
      if (response.data.status === 'paid' || response.data.status === 'already_paid') {
        alert('Payment successful! The shipper has been notified and can now proceed with their payment.');
        fetchCarrierData();
      } else {
        console.log('Adhoc payment verification:', response.data);
      }
    } catch (error) {
      console.error('Adhoc payment verification failed:', error);
      alert('Payment received! Please refresh the page to see updated status.');
    }
  };

  const fetchCarrierData = async () => {
    if (!carrierEmail) return;
    
    setLoading(true);
    setInitialLoading(true);
    try {
      const [quotesRes, loadsRes, subRes, docsRes, equipRes, locationsRes, bankRes] = await Promise.all([
        axios.get(`${API}/carrier/quotes/${carrierEmail}`).catch(() => ({ data: [] })),
        axios.get(`${API}/carrier/loads/${carrierEmail}`).catch(() => ({ data: [] })),
        axios.get(`${API}/carrier/subscription/${carrierEmail}`),
        axios.get(`${API}/insurance/carrier/${carrierEmail}`).catch(() => ({ data: [] })),
        axios.get(`${API}/equipment/carrier/${carrierEmail}`).catch(() => ({ data: [] })),
        axios.get(`${API}/users/${carrierEmail}/locations`).catch(() => ({ data: [] })),
        axios.get(`${API}/users/${carrierEmail}/bank-details`).catch(() => ({ data: {} }))
      ]);
      
      setQuotes(quotesRes.data);
      setLoads(loadsRes.data);
      setSubscriptionInfo(subRes.data);
      setDocuments(docsRes.data);
      setEquipment(equipRes.data);
      setCarrierProfile({
        base_locations: locationsRes.data,
        ...bankRes.data
      });
    } catch (error) {
      if (error.response?.status === 404) {
        alert('Carrier account not found. Please sign up first or check your email address.');
        setCarrierEmail('');
      } else {
        alert('Failed to load carrier data');
      }
      console.error(error);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const viewDocument = (documentId) => {
    // Extract the document ID from the stored:// URL if needed
    const docId = documentId.includes('stored://') ? documentId.split('/').pop() : documentId;
    window.open(`${API}/documents/view/${docId}`, '_blank');
  };

  const fetchCarrierHistory = async () => {
    try {
      const res = await axios.get(`${API}/carrier/history/${carrierEmail}`);
      setHistoryLoads(res.data);
    } catch (e) { console.error('Failed to fetch history:', e); }
  };

  const handleMarkCompleted = async (loadId) => {
    if (!window.confirm('Mark this load as completed? This will move it to your History.')) return;
    try {
      await axios.put(`${API}/loads/${loadId}/mark-completed`, { user_email: carrierEmail });
      alert('Load marked as completed!');
      fetchCarrierData();
      fetchCarrierHistory();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to mark as completed');
    }
  };


  const getStatusBadge = (status) => {
    const badges = {
      'pending_approval': { color: 'bg-yellow-100 text-yellow-800', text: 'Pending Approval' },
      'approved': { color: 'bg-blue-100 text-blue-800', text: 'Approved' },
      'accepted': { color: 'bg-green-100 text-green-800', text: 'Accepted' },
      'rejected': { color: 'bg-red-100 text-red-800', text: 'Rejected' },
      'declined': { color: 'bg-gray-100 text-gray-800', text: 'Declined' },
      'rescinded': { color: 'bg-orange-100 text-orange-800', text: 'Rescinded' },
      'in_transit': { color: 'bg-purple-100 text-purple-800', text: 'In Transit' },
      'delivered': { color: 'bg-green-100 text-green-800', text: 'Delivered' }
    };
    
    const badge = badges[status] || { color: 'bg-gray-100 text-gray-800', text: status };
    return <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badge.color}`}>{badge.text}</span>;
  };

  const handleReplaceDocument = async (doc) => {
    const fileInput = document.getElementById(`file-${doc.id}`);
    const policyInput = document.getElementById(`policy-${doc.id}`);
    const insurerInput = document.getElementById(`insurer-${doc.id}`);
    const expiryInput = document.getElementById(`expiry-${doc.id}`);

    if (!fileInput.files[0]) {
      alert('Please select a document to upload');
      return;
    }

    if (!policyInput.value || !expiryInput.value) {
      alert('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const file = fileInput.files[0];
      const reader = new FileReader();
      
      reader.onloadend = async () => {
        try {
          // Extract base64 data without the data URI prefix
          const base64Data = reader.result.split(',')[1];
          
          const uploadData = {
            carrier_email: carrierEmail,
            insurance_type: doc.insurance_type,
            policy_number: policyInput.value,
            insurer_name: insurerInput ? insurerInput.value : '',
            expiry_date: expiryInput.value,
            document_base64: base64Data,
            file_name: file.name
          };

          await axios.post(`${API}/insurance/upload`, uploadData);
          
          alert('Document updated successfully! It will be reviewed by our admin team.');
          setUploadingDoc(null);
          fetchCarrierData();
        } catch (error) {
          alert('Failed to upload document. Please try again.');
          console.error(error);
        } finally {
          setLoading(false);
        }
      };

      reader.readAsDataURL(file);
    } catch (error) {
      alert('Failed to upload document. Please try again.');
      console.error(error);
      setLoading(false);
    }
  };

  const availableDocTypes = [
    { key: 'public_liability', label: 'Public Liability Insurance', type: 'insurance', mandatory: true },
    { key: 'motor_fleet', label: 'Motor/Fleet Insurance', type: 'insurance', mandatory: true },
    { key: 'marine_carriers', label: 'Marine Carriers Insurance', type: 'insurance', mandatory: true },
    { key: 'workers_comp', label: 'Workers Compensation Insurance', type: 'insurance', mandatory: true, note: "Don't have Workers Compensation? Contact admin: 1300 380 700" },
    { key: 'trailer_photo', label: 'Rear 3/4 Photo of Trailer', type: 'photo', mandatory: true },
    { key: 'trailers_in_control', label: 'Trailers in Control Insurance', type: 'insurance' },
    { key: 'nhvr_accreditation', label: 'NHVR Accreditation Certificate', type: 'certificate' },
    { key: 'wahva_accreditation', label: 'WAHVA Accreditation Certificate', type: 'certificate' },
    { key: 'trucksafe_cor', label: 'TruckSafe/COR Certificate', type: 'certificate' },
    { key: 'haccp_accreditation', label: 'HACCP Accreditation Certificate', type: 'certificate' },
    { key: 'msic_pass', label: 'MSIC Pass', type: 'licence' },
    { key: 'dangerous_goods', label: 'Dangerous Goods Licencing', type: 'licence' },
    { key: 'ports_pass', label: 'Ports Pass', type: 'licence' },
    { key: 'high_risk_work', label: 'High Risk Work Licences', type: 'licence' }
  ];

  const getAvailableDocTypes = () => {
    const uploadedTypes = documents.map(doc => doc.insurance_type);
    return availableDocTypes.filter(type => !uploadedTypes.includes(type.key));
  };

  const handleAddNewDocument = async () => {
    const fileInput = document.getElementById('new-doc-file');
    const policyInput = document.getElementById('new-doc-policy');
    const insurerInput = document.getElementById('new-doc-insurer');
    const expiryInput = document.getElementById('new-doc-expiry');

    if (!newDocType) {
      alert('Please select a document type');
      return;
    }

    if (!fileInput.files[0]) {
      alert('Please select a document to upload');
      return;
    }

    const selectedDocType = availableDocTypes.find(d => d.key === newDocType);
    const isPhotoType = selectedDocType?.type === 'photo';

    // Validate based on document type
    if (!policyInput.value) {
      alert(isPhotoType ? 'Please enter a description' : 'Please fill in all required fields');
      return;
    }

    // Expiry date not required for photo types
    if (!isPhotoType && !expiryInput?.value) {
      alert('Please fill in all required fields');
      return;
    }

    if (selectedDocType.type === 'insurance' && !insurerInput?.value) {
      alert('Please enter insurer name for insurance documents');
      return;
    }

    setLoading(true);
    try {
      const file = fileInput.files[0];
      const reader = new FileReader();
      
      reader.onloadend = async () => {
        try {
          // Extract base64 data without the data URI prefix
          const base64Data = reader.result.split(',')[1];
          
          const uploadData = {
            carrier_email: carrierEmail,
            insurance_type: newDocType,
            policy_number: policyInput.value,
            insurer_name: insurerInput ? insurerInput.value : '',
            expiry_date: isPhotoType ? '2099-12-31' : expiryInput.value, // Photos don't expire, set far future date
            document_base64: base64Data,
            file_name: file.name
          };

          await axios.post(`${API}/insurance/upload`, uploadData);
          
          alert('Document uploaded successfully! It will be reviewed by our admin team.');
          setShowAddDocumentForm(false);
          setNewDocType('');
          fetchCarrierData();
        } catch (error) {
          alert('Failed to upload document. Please try again.');
          console.error(error);
        } finally {
          setLoading(false);
        }
      };

      reader.readAsDataURL(file);
    } catch (error) {
      alert('Failed to upload document. Please try again.');
      console.error(error);
      setLoading(false);
    }
  };
  const handleAddEquipment = async () => {
    const equipmentType = document.getElementById('equip-type').value;
    const registration = document.getElementById('equip-registration').value;
    const expiry = document.getElementById('equip-expiry').value;
    const tareMass = document.getElementById('equip-tare').value;
    const payloadRating = document.getElementById('equip-payload').value;
    const axles = document.getElementById('equip-axles').value;
    const tyres = document.getElementById('equip-tyres').value;
    const description = document.getElementById('equip-description').value;
    const photoInput = document.getElementById('equip-photo');
    const ratingLetterInput = document.getElementById('equip-rating-letter');

    if (!equipmentType || !registration || !expiry || !tareMass || !payloadRating || !axles || !tyres) {
      alert('Please fill in all required fields');
      return;
    }

    if (!photoInput.files[0]) {
      alert('Please upload a rear quarter photo showing the registration');
      return;
    }

    if (equipmentType === 'prime_mover' && !ratingLetterInput.files[0]) {
      alert('Please upload manufacturer\'s rating letter for Prime Mover');
      return;
    }

    setLoading(true);
    try {
      const photoFile = photoInput.files[0];
      const photoReader = new FileReader();
      
      photoReader.onloadend = async () => {
        const photoBase64 = photoReader.result;
        
        const equipmentData = {
          carrier_email: carrierEmail,
          equipment_type: equipmentType,
          description: description,
          registration_number: registration,
          registration_expiry: expiry,
          tare_mass: parseInt(tareMass),
          payload_rating: parseInt(payloadRating),
          number_of_axles: parseInt(axles),
          number_of_tyres: parseInt(tyres),
          photo_rear_quarter: photoBase64
        };

        // If prime mover, add rating letter
        if (equipmentType === 'prime_mover' && ratingLetterInput.files[0]) {
          const ratingLetterFile = ratingLetterInput.files[0];
          const ratingLetterReader = new FileReader();
          
          ratingLetterReader.onloadend = async () => {
            equipmentData.rating_letter_url = ratingLetterReader.result;
            
            await axios.post(`${API}/equipment`, equipmentData);
            
            alert('Prime Mover registered successfully with rating letter!');
            setShowAddEquipmentForm(false);
            setSelectedEquipmentType('');
            fetchCarrierData();
          };
          
          ratingLetterReader.readAsDataURL(ratingLetterFile);
        } else {
          await axios.post(`${API}/equipment`, equipmentData);
          
          alert('Vehicle/Trailer registered successfully!');
          setShowAddEquipmentForm(false);
          setSelectedEquipmentType('');
          fetchCarrierData();
        }
      };

      photoReader.readAsDataURL(photoFile);
    } catch (error) {
      alert('Failed to register vehicle/trailer. Please try again.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteEquipment = async () => {
    if (!equipToDelete) return;
    
    setLoading(true);
    try {
      console.log('Deleting equipment:', equipToDelete.id, 'for user:', carrierEmail);
      
      await axios.delete(`${API}/equipment/${equipToDelete.id}`, {
        params: { user_email: carrierEmail }
      });
      
      // Refresh equipment
      const equipRes = await axios.get(`${API}/equipment/carrier/${carrierEmail}`);
      setEquipment(equipRes.data);
      
      setShowDeleteEquipModal(false);
      setEquipToDelete(null);
    } catch (error) {
      console.error('Delete error:', error);
      alert(`Failed to delete equipment: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteDocument = async () => {
    if (!docToDelete) return;
    
    setLoading(true);
    try {
      console.log('Deleting document:', docToDelete.id, 'for user:', carrierEmail);
      
      await axios.delete(`${API}/insurance/${docToDelete.id}`, {
        params: { user_email: carrierEmail }
      });
      
      // Refresh documents
      const docsRes = await axios.get(`${API}/insurance/carrier/${carrierEmail}`);
      setDocuments(docsRes.data);
      
      setShowDeleteDocModal(false);
      setDocToDelete(null);
    } catch (error) {
      console.error('Delete error:', error);
      alert(`Failed to delete document: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyPromoCode = async () => {
    if (!promoCode.trim()) {
      alert('Please enter a promo code');
      return;
    }

    setApplyingPromo(true);
    try {
      const response = await axios.post(`${API}/promo-codes/apply-to-subscription`, {
        code: promoCode.trim(),
        user_email: carrierEmail
      });

      alert(response.data.message + `\n\nYour subscription has been extended by ${response.data.extension_months} month(s).\nNew expiry date: ${formatDateAU(response.data.new_expiry_date)}`);
      
      setPromoCode('');
      fetchCarrierData(); // Refresh to show updated subscription info
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to apply promo code';
      alert(`Error: ${errorMsg}`);
      console.error('Promo code error:', error);
    } finally {
      setApplyingPromo(false);
    }
  };

  // Handle adhoc payment with optional promo code
  const handleAdhocPayment = async (loadId) => {
    setProcessingAdhocPayment(loadId);
    try {
      const params = new URLSearchParams({
        load_id: loadId,
        carrier_email: carrierEmail
      });
      
      if (adhocPromoCode.trim()) {
        params.append('promo_code', adhocPromoCode.trim());
      }
      
      const response = await axios.post(`${API}/carrier/adhoc-payment/create-checkout?${params.toString()}`);
      
      if (response.data.status === 'free_quote') {
        // Promo code covered the full amount
        alert(response.data.message);
        setAdhocPromoCode('');
        fetchCarrierData(); // Refresh to show updated status
      } else if (response.data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to process payment';
      alert(`Error: ${errorMsg}`);
      console.error('Adhoc payment error:', error);
    } finally {
      setProcessingAdhocPayment(null);
    }
  };

  // Handle quote rescind
  const handleRescindQuote = async () => {
    if (!quoteToRescind || !rescindReason.trim()) {
      alert('Please provide a reason for rescinding the quote');
      return;
    }
    
    if (rescindReason.trim().length < 5) {
      alert('Please provide a more detailed reason (minimum 5 characters)');
      return;
    }
    
    setRescindingQuote(true);
    try {
      const params = new URLSearchParams({
        carrier_email: carrierEmail,
        reason: rescindReason.trim()
      });
      
      const response = await axios.post(`${API}/carrier/quotes/${quoteToRescind.id}/rescind?${params.toString()}`);
      alert(response.data.message || 'Quote rescinded successfully');
      setShowRescindModal(false);
      setQuoteToRescind(null);
      setRescindReason('');
      fetchCarrierData(); // Refresh quotes
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to rescind quote';
      alert(`Error: ${errorMsg}`);
      console.error('Rescind quote error:', error);
    } finally {
      setRescindingQuote(false);
    }
  };

  const canRescindQuote = (quote) => {
    // Can rescind if status is pending_approval, approved, or accepted (but not paid)
    const rescindableStatuses = ['pending_approval', 'approved', 'accepted'];
    return rescindableStatuses.includes(quote.status);
  };

  // Show loading screen while checking authentication
  if (initialLoading) {
    console.log('⏳ SHOWING LOADING SCREEN');
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 sm:px-6 py-12">
          <div className="max-w-md mx-auto">
            <Card className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1e3a5f] mx-auto mb-4"></div>
              <p className="text-gray-600">Loading your dashboard...</p>
            </Card>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Show authentication screen only if loading is complete and still no email
  console.log('=== AUTH CHECK ===');
  console.log('carrierEmail:', carrierEmail);
  console.log('initialLoading:', initialLoading);
  console.log('Should show auth screen:', !carrierEmail);
  
  if (!carrierEmail) {
    console.log('❌ SHOWING AUTHENTICATION REQUIRED');
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        
        <div className="container mx-auto px-4 sm:px-6 py-12">
          <div className="max-w-md mx-auto">
            <Card className="p-8 text-center">
              <h1 className="text-2xl font-bold text-[#1e3a5f] mb-4">Authentication Required</h1>
              <p className="text-gray-600 mb-6">
                Please log in to access your carrier dashboard.
              </p>
              
              <Button 
                onClick={() => navigate('/login')}
                className="w-full bg-red-600 hover:bg-red-700 text-white mb-4"
              >
                Go to Login Page
              </Button>
              
              <p className="text-sm text-gray-600">
                Don't have an account?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/signup?role=carrier')}
                  className="text-red-600 hover:text-red-700 font-medium"
                >
                  Sign Up
                </button>
              </p>
            </Card>
          </div>
        </div>
        
        <Footer />
      </div>
    );
  }
  
  console.log('✅ EMAIL FOUND, RENDERING DASHBOARD for:', carrierEmail);

  // Show loading state while fetching subscription data
  if (initialLoading || !subscriptionInfo) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        
        <div className="container mx-auto px-4 sm:px-6 py-12">
          <div className="max-w-md mx-auto">
            <Card className="p-8 text-center">
              <div className="flex flex-col items-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mb-4"></div>
                <h2 className="text-xl font-semibold text-gray-700">Loading your dashboard...</h2>
                <p className="text-gray-500 mt-2">Please wait while we fetch your information.</p>
              </div>
            </Card>
          </div>
        </div>
        
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="bg-[#1e3a5f] text-white py-12">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold mb-2">Carrier Dashboard</h1>
              <p className="text-lg text-gray-200">{subscriptionInfo.name}</p>
              <p className="text-sm text-gray-300">{subscriptionInfo.email}</p>
            </div>
            <div className="flex gap-3">
              <RoleSwitcher />
              <Button
                onClick={() => {
                  setCarrierEmail('');
                  setSubscriptionInfo(null);
                  setQuotes([]);
                  setLoads([]);
                  localStorage.removeItem('user_email');
                  localStorage.removeItem('user_roles');
                  localStorage.removeItem('active_role');
                  localStorage.removeItem('user_data');
                  navigate('/login');
                }}
                variant="outline"
                className="bg-white text-[#1e3a5f]"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-8">
        {/* Subscription Status */}
        <Card className={`p-6 mb-6 ${
          subscriptionInfo.is_adhoc ? 'bg-blue-50 border-blue-200' :
          subscriptionInfo.is_active ? 'bg-green-50 border-green-200' : 
          !subscriptionInfo.approved_carrier ? 'bg-yellow-50 border-yellow-200' :
          subscriptionInfo.payment_completed ? 'bg-green-50 border-green-200' : 
          'bg-blue-50 border-blue-200'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="font-bold text-lg mb-1">
                {subscriptionInfo.is_adhoc ? '✓ Adhoc Member' :
                 subscriptionInfo.is_active ? '✓ Active Subscription' : 
                 !subscriptionInfo.approved_carrier ? '⏳ Pending Approval' :
                 !subscriptionInfo.payment_completed ? '⏳ Subscription Pending' :
                 '✓ Active Subscription'}
              </h3>
              
              {/* Show subscription details for all states */}
              <div className="space-y-1">
                {subscriptionInfo.subscription_plan && (
                  <p className="text-sm text-gray-700">
                    <strong>Plan:</strong> <span className="capitalize">{subscriptionInfo.subscription_plan}</span>
                  </p>
                )}
                
                {/* Only show expiry for non-adhoc plans */}
                {subscriptionInfo.subscription_expiry && !subscriptionInfo.is_adhoc && (
                  <p className="text-sm text-gray-700">
                    <strong>Expires:</strong> {formatDateAU(subscriptionInfo.subscription_expiry)}
                    {new Date(subscriptionInfo.subscription_expiry) < new Date() && (
                      <span className="ml-2 text-red-600 font-semibold">(Expired)</span>
                    )}
                  </p>
                )}
                
                {subscriptionInfo.trial_end_date && !subscriptionInfo.is_adhoc && (
                  <p className="text-sm text-gray-700">
                    <strong>Trial Ends:</strong> {formatDateAU(subscriptionInfo.trial_end_date)}
                  </p>
                )}
                
                {/* Status message */}
                <p className="text-sm mt-2">
                  {subscriptionInfo.is_adhoc ? (
                    <span className="text-blue-700">✓ You can quote on any load. You only pay $49 per job when your quote is accepted.</span>
                  ) : subscriptionInfo.is_active && subscriptionInfo.approved_carrier ? (
                    <span className="text-green-700">✓ Your subscription is active and you are approved to quote on loads.</span>
                  ) : !subscriptionInfo.approved_carrier ? (
                    <span className="text-yellow-700">⏳ Your account is pending admin approval. No payment is required until you are approved.</span>
                  ) : subscriptionInfo.approved_carrier && !subscriptionInfo.payment_completed ? (
                    <span className="text-blue-700">✓ Your account is approved! Your subscription will be activated by admin. Contact 1300 380 700 if you need assistance.</span>
                  ) : (
                    <span className="text-green-700">✓ Your account is set up and ready to go.</span>
                  )}
                </p>
              </div>
            </div>
            {/* Subscription status message for non-active carriers */}
            {subscriptionInfo.approved_carrier && !subscriptionInfo.is_active && !subscriptionInfo.is_adhoc && !subscriptionInfo.payment_completed && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-2">
                <p className="text-sm text-blue-800">
                  <strong>Subscription Pending:</strong> Your account has been approved. Your subscription will be activated once admin processes your membership. 
                  If you have a promo code, contact admin at <a href="tel:1300380700" className="underline font-semibold">1300 380 700</a> or <a href="mailto:info@trucknsendit.com" className="underline font-semibold">info@trucknsendit.com</a> to have it applied.
                </p>
              </div>
            )}
          </div>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-6 mb-4 sm:mb-8">
          <Card className="p-3 sm:p-6">
            <div className="flex flex-col sm:flex-row items-center sm:items-center gap-1 sm:gap-3 text-center sm:text-left">
              <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-600" />
              <div>
                <p className="text-xl sm:text-2xl font-bold text-[#1e3a5f]">
                  {quotes.filter(q => q.status === 'pending_approval').length}
                </p>
                <p className="text-[10px] sm:text-sm text-gray-600">Pending</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-3 sm:p-6">
            <div className="flex flex-col sm:flex-row items-center sm:items-center gap-1 sm:gap-3 text-center sm:text-left">
              <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
              <div>
                <p className="text-xl sm:text-2xl font-bold text-[#1e3a5f]">
                  {quotes.filter(q => q.status === 'approved').length}
                </p>
                <p className="text-[10px] sm:text-sm text-gray-600">Approved</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-3 sm:p-6">
            <div className="flex flex-col sm:flex-row items-center sm:items-center gap-1 sm:gap-3 text-center sm:text-left">
              <Truck className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
              <div>
                <p className="text-xl sm:text-2xl font-bold text-[#1e3a5f]">{loads.length}</p>
                <p className="text-[10px] sm:text-sm text-gray-600">Active Loads</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-3 sm:p-6">
            <div className="flex flex-col sm:flex-row items-center sm:items-center gap-1 sm:gap-3 text-center sm:text-left">
              <Package className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600" />
              <div>
                <p className="text-xl sm:text-2xl font-bold text-[#1e3a5f]">
                  {loads.filter(l => l.status === 'delivered').length}
                </p>
                <p className="text-[10px] sm:text-sm text-gray-600">Completed</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Base Location Prompt */}
        {carrierProfile && (!carrierProfile.base_locations || carrierProfile.base_locations.length === 0) && (
          <Card className="p-4 mb-4 bg-amber-50 border-amber-300 border-l-4" data-testid="base-location-prompt">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <div className="flex-1">
                <p className="font-semibold text-amber-900 text-sm sm:text-base">Add your base location(s) to get load alerts!</p>
                <p className="text-xs sm:text-sm text-amber-800 mt-1">
                  You'll be notified when loads are posted within 200km of your base locations. You can add up to 12 locations.
                </p>
              </div>
              <Button
                onClick={() => setActiveTab('settings')}
                className="bg-amber-600 hover:bg-amber-700 text-white text-xs sm:text-sm whitespace-nowrap"
                data-testid="update-locations-btn"
              >
                Update Locations
              </Button>
            </div>
          </Card>
        )}

        {/* Section Selector - button grid on mobile, tabs on desktop */}
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-4 sm:mb-6">
          <button
            onClick={() => setActiveTab('quotes')}
            className={`py-2 px-2 rounded-lg text-xs sm:text-sm font-medium border transition-colors ${
              activeTab === 'quotes' 
                ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' 
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
            data-testid="carrier-tab-quotes"
          >
            Quotes ({quotes.length})
          </button>
          <button
            onClick={() => setActiveTab('loads')}
            className={`py-2 px-2 rounded-lg text-xs sm:text-sm font-medium border transition-colors ${
              activeTab === 'loads' 
                ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' 
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
            data-testid="carrier-tab-loads"
          >
            Loads ({loads.length})
          </button>
          <button
            onClick={() => setActiveTab('equipment')}
            className={`py-2 px-2 rounded-lg text-xs sm:text-sm font-medium border transition-colors ${
              activeTab === 'equipment' 
                ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' 
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
            data-testid="carrier-tab-equipment"
          >
            Equipment
          </button>
          <button
            onClick={() => setActiveTab('documents')}
            className={`relative py-2 px-2 rounded-lg text-xs sm:text-sm font-medium border transition-colors ${
              activeTab === 'documents' 
                ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' 
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
            data-testid="carrier-tab-documents"
          >
            Docs
            {documents.some(doc => {
              if (doc.insurance_type === 'trailer_photo') return false;
              const daysUntilExpiry = Math.ceil((new Date(doc.expiry_date) - new Date()) / (1000 * 60 * 60 * 24));
              return daysUntilExpiry <= 14;
            }) && (
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`py-2 px-2 rounded-lg text-xs sm:text-sm font-medium border transition-colors ${
              activeTab === 'settings' 
                ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' 
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
            data-testid="carrier-tab-settings"
          >
            Settings
          </button>
          {(subscriptionInfo.is_active || subscriptionInfo.is_adhoc) && (
            <button
              onClick={() => setActiveTab('compliance')}
              className={`py-2 px-2 rounded-lg text-xs sm:text-sm font-medium border transition-colors ${
                activeTab === 'compliance' 
                  ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' 
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
              data-testid="carrier-tab-compliance"
            >
              Compliance
            </button>
          )}
          {subscriptionInfo.is_active && !subscriptionInfo.is_adhoc && (
            <button
              onClick={() => setActiveTab('availability')}
              className={`py-2 px-2 rounded-lg text-xs sm:text-sm font-medium border transition-colors ${
                activeTab === 'availability' 
                  ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' 
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
              data-testid="carrier-tab-availability"
            >
              Availability
            </button>
          )}
          <button
            onClick={() => navigate('/carrier/trailer-hire-bookings')}
            className="py-2 px-2 rounded-lg text-xs sm:text-sm font-medium border transition-colors bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
            data-testid="carrier-tab-trailer-hire"
          >
            Truck &amp; Trailer Hire →
          </button>
          <button
            onClick={() => { setActiveTab('history'); fetchCarrierHistory(); }}
            className={`py-2 px-2 rounded-lg text-xs sm:text-sm font-medium border transition-colors ${
              activeTab === 'history' 
                ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' 
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
            data-testid="carrier-tab-history"
          >
            History
          </button>
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === 'quotes' && (<>
            <CarrierNegotiationsPanel
              carrierEmail={carrierEmail}
              highlightNegotiationId={searchParams.get('negotiation_id')}
            />
            {quotes.length === 0 ? (
              <Card className="p-12 text-center">
                <p className="text-gray-600 mb-4">You haven't submitted any quotes yet</p>
                <Button onClick={() => navigate('/browse-loads')} className="bg-red-600 hover:bg-red-700 text-white">
                  Browse Available Loads
                </Button>
              </Card>
            ) : (
              <div className="space-y-4">
                {quotes.map((quote) => (
                  <Card key={quote.id} className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-bold text-lg text-[#1e3a5f] mb-1">
                          {quote.load_details.cargo_type}
                        </h3>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <MapPin className="w-4 h-4" />
                          <span>{quote.load_details.pickup_location} → {quote.load_details.dropoff_location}</span>
                        </div>
                      </div>
                      {getStatusBadge(quote.status)}
                    </div>

                    <div className="grid md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <p className="text-sm text-gray-600">Your Quote</p>
                        <p className="font-bold text-lg">${quote.original_amount || quote.amount}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Delivery Time</p>
                        <p className="font-semibold">{quote.estimated_delivery_days} days</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Submitted</p>
                      <p className="font-semibold text-sm">{formatDateAU(quote.created_at)}</p>
                      </div>
                    </div>

                    {quote.message && (
                      <div className="bg-gray-50 p-3 rounded text-sm text-gray-700 mb-3">
                        <strong>Your message:</strong> {quote.message}
                      </div>
                    )}

                    {quote.rejection_reason && (
                      <div className="bg-red-50 p-3 rounded text-sm text-red-700 mb-3">
                        <strong>Rejection reason:</strong> {quote.rejection_reason}
                      </div>
                    )}

                    {quote.rescind_reason && (
                      <div className="bg-orange-50 p-3 rounded text-sm text-orange-700 mb-3">
                        <strong>Rescind reason:</strong> {quote.rescind_reason}
                      </div>
                    )}

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/load/${quote.load_id}`)}
                      >
                        View Load Details
                      </Button>
                      
                      {canRescindQuote(quote) && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-orange-600 border-orange-300 hover:bg-orange-50"
                          onClick={() => {
                            setQuoteToRescind(quote);
                            setShowRescindModal(true);
                          }}
                          data-testid={`rescind-quote-${quote.id}`}
                        >
                          Rescind Quote
                        </Button>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </>)}

          {/* Loads Tab */}
          {activeTab === 'loads' && (<>
            {loads.length === 0 ? (
              <Card className="p-12 text-center">
                <p className="text-gray-600">No accepted loads yet</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {loads.map((load) => (
                  <Card key={load.id} className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-bold text-lg text-[#1e3a5f] mb-1">{load.cargo_type}</h3>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <MapPin className="w-4 h-4" />
                          <span>{load.pickup_location} → {load.dropoff_location}</span>
                        </div>
                      </div>
                      {getStatusBadge(load.status)}
                    </div>

                    <div className="grid md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <p className="text-sm text-gray-600">Payment Amount</p>
                        <p className="font-bold text-lg">${load.payment_amount?.toFixed(2) || 'N/A'}</p>
                      </div>
                      {load.weight && (
                        <div>
                          <p className="text-sm text-gray-600">Weight</p>
                          <p className="font-semibold">{load.weight} kg</p>
                        </div>
                      )}
                      {load.pickup_date && (
                        <div>
                          <p className="text-sm text-gray-600">Available From</p>
                          <p className="font-semibold text-sm">{formatDateAU(load.pickup_date)}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-sm text-gray-600">Load ID</p>
                        <p className="font-mono text-xs">{load.id.slice(0, 8)}</p>
                      </div>
                    </div>

                    {/* Adhoc Payment Required - for adhoc carriers */}
                    {load.awaiting_adhoc_payment && !load.adhoc_payment_completed && (
                      <div className="bg-orange-50 p-4 rounded mb-3 border border-orange-200">
                        <h4 className="font-semibold text-orange-800 mb-2">Job Fee Required - $49</h4>
                        <p className="text-sm text-orange-700 mb-3">
                          Your quote has been accepted! Pay the job fee to confirm the booking and reveal shipper contact details.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-2">
                          <Input
                            type="text"
                            placeholder="Promo code (optional)"
                            value={adhocPromoCode}
                            onChange={(e) => setAdhocPromoCode(e.target.value.toUpperCase())}
                            className="flex-1 sm:max-w-xs"
                            data-testid="adhoc-promo-input"
                          />
                          <Button
                            onClick={() => handleAdhocPayment(load.id)}
                            className="bg-orange-600 hover:bg-orange-700"
                            disabled={processingAdhocPayment === load.id}
                            data-testid="adhoc-pay-btn"
                          >
                            {processingAdhocPayment === load.id ? 'Processing...' : 'Pay $49 & Confirm'}
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Shipper Contact - shown ONLY after shipper payment completed */}
                    {(load.shipper_contact && (load.payment_completed || load.status === 'paid' || load.status === 'PAID')) && (
                      <div className="bg-green-50 p-4 rounded mb-3 border border-green-200">
                        <h4 className="font-semibold text-green-800 mb-2">Shipper Contact Details</h4>
                        <p className="text-sm text-green-700"><strong>Name:</strong> {load.shipper_contact.name}</p>
                        <p className="text-sm text-green-700"><strong>Email:</strong> {load.shipper_contact.email}</p>
                        <p className="text-sm text-green-700"><strong>Phone:</strong> {load.shipper_contact.phone}</p>
                        {load.shipper_contact.company_name && (
                          <p className="text-sm text-green-700"><strong>Company:</strong> {load.shipper_contact.company_name}</p>
                        )}
                        <p className="text-xs text-green-600 mt-2">Payment completed - you may contact the shipper directly</p>
                      </div>
                    )}

                    {/* Awaiting Payment Notice - show until shipper pays (not for adhoc awaiting their payment) */}
                    {!load.payment_completed && load.status !== 'paid' && load.status !== 'PAID' && !load.awaiting_adhoc_payment && (
                      <div className="bg-yellow-50 p-4 rounded mb-3 border border-yellow-200">
                        <p className="text-sm text-yellow-800">
                          <strong>Awaiting Shipper Payment:</strong> Contact details will be revealed once the shipper completes payment.
                        </p>
                      </div>
                    )}

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/load/${load.id}`)}
                      >
                        View Details
                      </Button>
                      {(load.payment_completed || load.status === 'paid' || load.status === 'in_transit') && load.status !== 'delivered' && (
                        <Button
                          size="sm"
                          onClick={() => handleMarkCompleted(load.id)}
                          className="bg-green-600 hover:bg-green-700 text-white"
                          data-testid={`carrier-mark-complete-${load.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" /> Mark as Completed
                        </Button>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </>)}



          {/* Equipment Tab */}
          {activeTab === 'equipment' && (<>
            {/* --- My Truck & Trailer Hire Listings --- */}
            <Card className="p-6 mb-4" data-testid="my-hire-listings-section">
              <div className="flex justify-between items-start flex-wrap gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-[#1e3a5f] mb-2 flex items-center gap-2">
                    <Truck className="w-5 h-5 text-amber-600" /> My Truck &amp; Trailer Hire Listings
                  </h3>
                  <p className="text-sm text-gray-600">
                    Trucks and trailers you've listed on the public hire marketplace. Edit or remove any time.
                  </p>
                </div>
                <Button
                  onClick={() => navigate('/list-trailer')}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="list-new-hire-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  List New Truck/Trailer
                </Button>
              </div>

              <div className="mt-5">
                {loadingHireListings ? (
                  <p className="text-sm text-gray-500">Loading listings…</p>
                ) : hireListings.length === 0 ? (
                  <div className="text-center py-6 border border-dashed border-gray-300 rounded-lg bg-gray-50">
                    <Truck className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">You haven't listed any trucks or trailers for hire yet.</p>
                    <Button
                      onClick={() => navigate('/list-trailer')}
                      variant="outline"
                      className="mt-3"
                      data-testid="list-first-hire-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" /> List your first truck/trailer
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3" data-testid="my-hire-listings">
                    {hireListings.map((l) => {
                      const isTruck = l.vehicle_category === 'truck';
                      const title = isTruck
                        ? (l.truck_type === 'Other' ? l.truck_type_other : l.truck_type) || 'Truck'
                        : l.trailer_type || 'Trailer';
                      const statusTone =
                        l.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : l.status === 'paused'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-200 text-gray-700';
                      return (
                        <div
                          key={l.id}
                          className="border border-gray-200 rounded-lg p-3 md:p-4 flex items-start justify-between gap-3 flex-wrap hover:border-gray-300"
                          data-testid={`my-hire-listing-${l.id}`}
                        >
                          <div className="flex-1 min-w-[200px]">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <span className={`text-[10px] tracking-wider font-bold uppercase px-2 py-0.5 rounded ${isTruck ? 'bg-amber-100 text-amber-800 border border-amber-300' : 'bg-blue-100 text-blue-800 border border-blue-300'}`}>
                                {isTruck ? 'Truck' : 'Trailer'}
                              </span>
                              <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded ${statusTone}`}>
                                {l.status || 'active'}
                              </span>
                            </div>
                            <h4 className="font-semibold text-[#1e3a5f]">{title}</h4>
                            <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                              <MapPin className="w-3 h-3" /> {l.base_location || '—'}
                            </p>
                            <div className="text-xs text-gray-600 mt-1 space-x-3">
                              {l.indicative_daily_rate != null && l.indicative_daily_rate !== '' && (
                                <span>Daily: <strong>${l.indicative_daily_rate}</strong></span>
                              )}
                              {l.indicative_weekly_rate != null && l.indicative_weekly_rate !== '' && (
                                <span>Weekly: <strong>${l.indicative_weekly_rate}</strong></span>
                              )}
                              {l.indicative_monthly_rate != null && l.indicative_monthly_rate !== '' && (
                                <span>Monthly: <strong>${l.indicative_monthly_rate}</strong></span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/trailer-hire/${l.id}`)}
                              data-testid={`view-my-hire-${l.id}`}
                            >
                              View
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openHireEdit(l)}
                              data-testid={`edit-my-hire-${l.id}`}
                            >
                              <Edit2 className="w-4 h-4 mr-1" /> Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:text-red-700 border-red-300"
                              onClick={() => setHireDeleteTarget(l)}
                              data-testid={`delete-my-hire-${l.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </Card>

            <Card className="p-6 mb-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-semibold text-[#1e3a5f] mb-2">My Vehicles & Trailers</h3>
                  <p className="text-sm text-gray-600">
                    Register and manage your fleet. Add unlimited vehicles and trailers to your account.
                  </p>
                </div>
                <Button
                  onClick={() => setShowAddEquipmentForm(!showAddEquipmentForm)}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Register Vehicle/Trailer
                </Button>
              </div>
            </Card>

            {/* Add Equipment Form */}
            {showAddEquipmentForm && (
              <Card className="p-6 mb-4 bg-green-50 border border-green-200">
                <h4 className="font-semibold text-lg text-green-900 mb-4">Register New Vehicle/Trailer</h4>
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Vehicle/Trailer Type *</Label>
                      <select
                        id="equip-type"
                        value={selectedEquipmentType}
                        onChange={(e) => setSelectedEquipmentType(e.target.value)}
                        className="w-full p-2 border rounded"
                      >
                        <option value="">Select type</option>
                        <option value="prime_mover">Prime Mover</option>
                        <option value="flat_top_open">Flat Top (open)</option>
                        <option value="tautliner">Tautliner</option>
                        <option value="pantech">Pantech</option>
                        <option value="refrigerated">Refrigerated</option>
                        <option value="dropdeck_open">Dropdeck (open)</option>
                        <option value="dropdeck_ramps">Dropdeck with ramps</option>
                        <option value="low_loader">Low Loader</option>
                        <option value="platform">Platform</option>
                        <option value="dolly">Dolly</option>
                        <option value="tanker">Tanker</option>
                        <option value="pneumatic_tanker">Pneumatic Tanker</option>
                        <option value="stock_crate">Stock Crate</option>
                        <option value="tilt_tray">Tilt Tray</option>
                        <option value="end_tipper">End Tipper</option>
                        <option value="side_tipper">Side Tipper</option>
                        <option value="side_loader">Side Loader</option>
                        <option value="tank_trailer">Tank Trailer</option>
                        <option value="jinker">Jinker</option>
                        <option value="extendable_dropdeck">Extendable Dropdeck</option>
                        <option value="special_purpose">Special Purpose</option>
                        <option value="car_carrier">Car Carrier</option>
                      </select>
                    </div>

                    <div>
                      <Label>Registration Number *</Label>
                      <Input
                        id="equip-registration"
                        placeholder="e.g., ABC123"
                      />
                    </div>

                    <div>
                      <Label>Registration Expiry Date *</Label>
                      <Input
                        id="equip-expiry"
                        type="date"
                      />
                    </div>

                    <div>
                      <Label>Tare Mass (kg) *</Label>
                      <Input
                        id="equip-tare"
                        type="number"
                        placeholder="e.g., 8500"
                      />
                    </div>

                    <div>
                      <Label>Payload/Rating (kg) *</Label>
                      <Input
                        id="equip-payload"
                        type="number"
                        placeholder="e.g., 12000"
                      />
                    </div>

                    <div>
                      <Label>Number of Axles *</Label>
                      <Input
                        id="equip-axles"
                        type="number"
                        placeholder="e.g., 3"
                        min="1"
                      />
                    </div>

                    <div>
                      <Label>Number of Tyres *</Label>
                      <Input
                        id="equip-tyres"
                        type="number"
                        placeholder="e.g., 12"
                        min="1"
                      />
                    </div>

                    <div>
                      <Label>Description (Optional)</Label>
                      <Input
                        id="equip-description"
                        placeholder="e.g., 2020 Kenworth T610"
                      />
                    </div>
                  </div>

                  {selectedEquipmentType === 'prime_mover' && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
                      <Label className="text-yellow-900">Manufacturer's Rating Letter *</Label>
                      <Input
                        id="equip-rating-letter"
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="cursor-pointer mt-2"
                      />
                      <p className="text-xs text-yellow-800 mt-1">
                        Prime Movers require a manufacturer's rating letter to be uploaded
                      </p>
                    </div>
                  )}

                  <div>
                    <Label>Rear Quarter Photo (Registration Visible) *</Label>
                    <Input
                      id="equip-photo"
                      type="file"
                      accept="image/*"
                      className="cursor-pointer"
                    />
                    <p className="text-xs text-gray-600 mt-1">
                      Photo must clearly show registration number
                    </p>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={handleAddEquipment}
                      disabled={loading}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      {loading ? 'Registering...' : 'Register Vehicle/Trailer'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowAddEquipmentForm(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            {/* Equipment List */}
            {equipment.length === 0 ? (
              <Card className="p-12 text-center">
                <p className="text-gray-600 mb-4">No vehicles/trailers registered yet</p>
                <Button
                  onClick={() => setShowAddEquipmentForm(true)}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Register Your First Vehicle/Trailer
                </Button>
              </Card>
            ) : (
              <div className="space-y-4">
                {equipment.map((equip) => (
                  <Card key={equip.id} className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-semibold text-lg text-[#1e3a5f] capitalize">
                            {equip.equipment_type.replace(/_/g, ' ')}
                          </h4>
                          {equip.verified ? (
                            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                              Verified
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                              Pending Verification
                            </span>
                          )}
                        </div>

                        {equip.description && (
                          <p className="text-gray-600 text-sm mb-3">{equip.description}</p>
                        )}

                        <div className="grid md:grid-cols-3 gap-4 text-sm mb-4">
                          <div>
                            <p className="text-gray-600">Registration</p>
                            <p className="font-medium">{equip.registration_number}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Registration Expiry</p>
                            <p className={`font-medium ${
                              new Date(equip.registration_expiry) < new Date() ? 'text-red-600' : 'text-green-600'
                            }`}>
                              {equip.registration_expiry}
                              {new Date(equip.registration_expiry) < new Date() && ' (EXPIRED)'}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-600">Tare Mass</p>
                            <p className="font-medium">{equip.tare_mass ? `${equip.tare_mass} kg` : 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Payload Rating</p>
                            <p className="font-medium">{equip.payload_rating ? `${equip.payload_rating} kg` : 'N/A'}</p>
                          </div>
                          {equip.equipment_type === 'prime_mover' && equip.rating_letter_url && (
                            <div>
                              <p className="text-gray-600">Rating Letter</p>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => viewDocument(equip.id)}
                                className="mt-1"
                              >
                                View Document
                              </Button>
                            </div>
                          )}
                        </div>

                        {equip.photo_rear_quarter && (
                          <div className="mb-4">
                            <p className="text-sm text-gray-600 mb-2">Registration Photo:</p>
                            <img 
                              src={equip.photo_rear_quarter.startsWith('stored://') || equip.photo_rear_quarter.startsWith('/api/') 
                                ? `${API}/documents/view/${equip.id}` 
                                : equip.photo_rear_quarter
                              } 
                              alt="Vehicle registration" 
                              className="max-w-xs rounded border cursor-pointer hover:opacity-80"
                              onClick={() => viewDocument(equip.id)}
                            />
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex gap-3 mt-4 pt-4 border-t">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              // TODO: Implement edit modal
                              toast.info('Edit functionality coming soon');
                            }}
                            className="text-blue-600 border-blue-300"
                          >
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEquipToDelete(equip);
                              setShowDeleteEquipModal(true);
                            }}
                            className="text-red-600 border-red-300"
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </>)}

          {/* Documents Tab */}
          {activeTab === 'documents' && (<>
            {/* Document Expiration Warning Banner */}
            {(() => {
              const expiringDocs = documents.filter(doc => {
                if (doc.insurance_type === 'trailer_photo') return false; // Photos don't expire
                const expiryDate = new Date(doc.expiry_date);
                const now = new Date();
                const daysUntilExpiry = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
                return daysUntilExpiry <= 14; // Within 14 days or already expired
              });
              
              const expiredDocs = expiringDocs.filter(doc => new Date(doc.expiry_date) < new Date());
              const soonToExpireDocs = expiringDocs.filter(doc => new Date(doc.expiry_date) >= new Date());
              
              if (expiringDocs.length === 0) return null;
              
              return (
                <Card className={`p-4 mb-4 border-2 ${expiredDocs.length > 0 ? 'bg-red-50 border-red-300' : 'bg-yellow-50 border-yellow-300'}`}>
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-full ${expiredDocs.length > 0 ? 'bg-red-100' : 'bg-yellow-100'}`}>
                      <XCircle className={`w-6 h-6 ${expiredDocs.length > 0 ? 'text-red-600' : 'text-yellow-600'}`} />
                    </div>
                    <div className="flex-1">
                      <h4 className={`font-bold ${expiredDocs.length > 0 ? 'text-red-800' : 'text-yellow-800'}`}>
                        {expiredDocs.length > 0 ? '⚠️ Document(s) Expired!' : '⏰ Document(s) Expiring Soon'}
                      </h4>
                      <div className="mt-2 space-y-1">
                        {expiredDocs.length > 0 && (
                          <div>
                            <p className="text-red-700 text-sm font-semibold">Expired:</p>
                            <ul className="list-disc list-inside text-red-700 text-sm">
                              {expiredDocs.map(doc => (
                                <li key={doc.id}>
                                  {availableDocTypes.find(t => t.key === doc.insurance_type)?.label || doc.insurance_type} - 
                                  Expired on {formatDateAU(doc.expiry_date)}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {soonToExpireDocs.length > 0 && (
                          <div className={expiredDocs.length > 0 ? 'mt-2' : ''}>
                            <p className="text-yellow-700 text-sm font-semibold">Expiring within 14 days:</p>
                            <ul className="list-disc list-inside text-yellow-700 text-sm">
                              {soonToExpireDocs.map(doc => {
                                const daysLeft = Math.ceil((new Date(doc.expiry_date) - new Date()) / (1000 * 60 * 60 * 24));
                                return (
                                  <li key={doc.id}>
                                    {availableDocTypes.find(t => t.key === doc.insurance_type)?.label || doc.insurance_type} - 
                                    Expires in {daysLeft} day{daysLeft !== 1 ? 's' : ''} ({formatDateAU(doc.expiry_date)})
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        )}
                      </div>
                      <p className={`mt-3 text-sm ${expiredDocs.length > 0 ? 'text-red-600' : 'text-yellow-600'}`}>
                        <strong>Action required:</strong> Please upload updated documents to continue quoting on loads.
                        {expiredDocs.length > 0 && ' Expired documents may prevent you from submitting quotes.'}
                      </p>
                    </div>
                  </div>
                </Card>
              );
            })()}

            <Card className="p-6 mb-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-semibold text-[#1e3a5f] mb-2">Manage Your Documents</h3>
                  <p className="text-sm text-gray-600">
                    View and update your insurance and certification documents. Expired or updated documents should be replaced to maintain compliance.
                  </p>
                </div>
                {getAvailableDocTypes().length > 0 && (
                  <Button
                    onClick={() => setShowAddDocumentForm(!showAddDocumentForm)}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Document
                  </Button>
                )}
              </div>
            </Card>

            {/* Add New Document Form */}
            {showAddDocumentForm && (
              <Card className="p-6 mb-4 bg-green-50 border border-green-200">
                <h4 className="font-semibold text-lg text-green-900 mb-4">Add New Document</h4>
                <div className="space-y-4">
                  <div>
                    <Label>Document Type *</Label>
                    <select
                      id="new-doc-type"
                      value={newDocType}
                      onChange={(e) => setNewDocType(e.target.value)}
                      className="w-full p-2 border rounded"
                    >
                      <option value="">Select document type</option>
                      {getAvailableDocTypes().map(type => (
                        <option key={type.key} value={type.key}>
                          {type.label} {type.mandatory ? '(Required)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Show note for Workers Comp */}
                  {newDocType === 'workers_comp' && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-sm text-blue-800">
                        <strong>Note:</strong> Don't have Workers Compensation Insurance? 
                        <br />
                        Contact admin at <a href="tel:1300380700" className="font-bold underline">1300 380 700</a> to discuss exemptions.
                      </p>
                    </div>
                  )}

                  {newDocType && (
                    <>
                      {/* Only show policy/licence number for non-photo types */}
                      {availableDocTypes.find(d => d.key === newDocType)?.type !== 'photo' && (
                        <div>
                          <Label>
                            {availableDocTypes.find(d => d.key === newDocType)?.type === 'insurance' 
                              ? 'Policy Number *' 
                              : availableDocTypes.find(d => d.key === newDocType)?.type === 'certificate'
                              ? 'Accreditation Number *'
                              : 'Licence Number *'}
                          </Label>
                          <Input
                            id="new-doc-policy"
                            placeholder="Enter number"
                          />
                        </div>
                      )}

                      {/* Photo type gets a description field instead */}
                      {availableDocTypes.find(d => d.key === newDocType)?.type === 'photo' && (
                        <div>
                          <Label>Description *</Label>
                          <Input
                            id="new-doc-policy"
                            placeholder="e.g., 2020 Kenworth T610 Trailer"
                          />
                        </div>
                      )}

                      {availableDocTypes.find(d => d.key === newDocType)?.type === 'insurance' && (
                        <div>
                          <Label>Insurer Name *</Label>
                          <Input
                            id="new-doc-insurer"
                            placeholder="Enter insurer name"
                          />
                        </div>
                      )}

                      {/* Only show expiry date for non-photo types */}
                      {availableDocTypes.find(d => d.key === newDocType)?.type !== 'photo' && (
                        <div>
                          <Label>Expiry Date *</Label>
                          <Input
                            id="new-doc-expiry"
                            type="date"
                          />
                        </div>
                      )}

                      <div>
                        <Label>
                          {availableDocTypes.find(d => d.key === newDocType)?.type === 'photo'
                            ? 'Upload Photo (JPG, PNG) *'
                            : 'Upload Document (PDF, JPG, PNG) *'}
                        </Label>
                        <Input
                          id="new-doc-file"
                          type="file"
                          accept={availableDocTypes.find(d => d.key === newDocType)?.type === 'photo'
                            ? ".jpg,.jpeg,.png"
                            : ".pdf,.jpg,.jpeg,.png"}
                          className="cursor-pointer"
                        />
                        {availableDocTypes.find(d => d.key === newDocType)?.type === 'photo' && (
                          <p className="text-xs text-gray-600 mt-1">
                            Photo must clearly show the rear 3/4 view of your trailer with registration visible
                          </p>
                        )}
                      </div>

                      <div className="flex gap-2">
                        <Button
                          onClick={handleAddNewDocument}
                          disabled={loading}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          {loading ? 'Uploading...' : 'Upload Document'}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setShowAddDocumentForm(false);
                            setNewDocType('');
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </Card>
            )}

            {documents.length === 0 ? (
              <Card className="p-12 text-center">
                <p className="text-gray-600 mb-4">No documents uploaded yet</p>
                <Button
                  onClick={() => setShowAddDocumentForm(true)}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Document
                </Button>
              </Card>
            ) : (
              <div className="space-y-4">
                {documents.map((doc) => (
                  <Card key={doc.id} className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-semibold text-lg text-[#1e3a5f]">
                            {doc.insurance_type.replace(/_/g, ' ').toUpperCase()}
                          </h4>
                          {doc.verified ? (
                            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                              Verified
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                              Pending Verification
                            </span>
                          )}
                        </div>

                        <div className="grid md:grid-cols-3 gap-4 text-sm mb-4">
                          <div>
                            <p className="text-gray-600">
                              {doc.insurance_type === 'trailer_photo' ? 'Description' : 'Policy/Licence Number'}
                            </p>
                            <p className="font-medium">{doc.policy_number}</p>
                          </div>
                          {doc.insurer_name && (
                            <div>
                              <p className="text-gray-600">Insurer</p>
                              <p className="font-medium">{doc.insurer_name}</p>
                            </div>
                          )}
                          {/* Don't show expiry for photo types */}
                          {doc.insurance_type !== 'trailer_photo' && (
                            <div>
                              <p className="text-gray-600">Expiry Date</p>
                              <p className={`font-medium ${
                                new Date(doc.expiry_date) < new Date() ? 'text-red-600' : 
                                new Date(doc.expiry_date) < new Date(Date.now() + 30*24*60*60*1000) ? 'text-yellow-600' : 
                                'text-green-600'
                              }`}>
                                {doc.expiry_date}
                                {new Date(doc.expiry_date) < new Date() && ' (EXPIRED)'}
                              </p>
                            </div>
                          )}
                        </div>

                        <div className="flex gap-3">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => viewDocument(doc.id)}
                          >
                            View Document
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setUploadingDoc(doc.id)}
                            className="text-blue-600 border-blue-300"
                          >
                            Replace Document
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setDocToDelete(doc);
                              setShowDeleteDocModal(true);
                            }}
                            className="text-red-600 border-red-300"
                          >
                            Delete
                          </Button>
                        </div>

                        {uploadingDoc === doc.id && (
                          <div className="mt-4 p-4 bg-blue-50 rounded border border-blue-200">
                            <h5 className="font-semibold text-sm mb-3">Upload New Document</h5>
                            <div className="space-y-3">
                              <div>
                                <Label>Policy/Licence Number</Label>
                                <Input
                                  id={`policy-${doc.id}`}
                                  placeholder="Enter new number"
                                  defaultValue={doc.policy_number}
                                />
                              </div>
                              {doc.insurer_name && (
                                <div>
                                  <Label>Insurer Name</Label>
                                  <Input
                                    id={`insurer-${doc.id}`}
                                    placeholder="Enter insurer name"
                                    defaultValue={doc.insurer_name}
                                  />
                                </div>
                              )}
                              <div>
                                <Label>Expiry Date</Label>
                                <Input
                                  id={`expiry-${doc.id}`}
                                  type="date"
                                  defaultValue={doc.expiry_date}
                                />
                              </div>
                              <div>
                                <Label>Upload Document (PDF, JPG, PNG)</Label>
                                <Input
                                  id={`file-${doc.id}`}
                                  type="file"
                                  accept=".pdf,.jpg,.jpeg,.png"
                                  className="cursor-pointer"
                                />
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  onClick={() => handleReplaceDocument(doc)}
                                  className="bg-blue-600 hover:bg-blue-700 text-white"
                                >
                                  Upload New Document
                                </Button>
                                <Button
                                  variant="outline"
                                  onClick={() => setUploadingDoc(null)}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </>)}


          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <Card className="p-4 sm:p-6">
              <h2 className="text-xl sm:text-2xl font-bold text-[#1e3a5f] mb-4 sm:mb-6">Account Settings</h2>
              
              {/* Base Locations Section */}
              <div className="mb-8 pb-8 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Base Locations</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Add up to 12 base locations. You'll receive email alerts when loads are posted within 200km of any of your locations.
                </p>
                
                {carrierProfile && carrierProfile.base_locations && carrierProfile.base_locations.length > 0 ? (
                  <div className="space-y-2 mb-4">
                    {carrierProfile.base_locations.map((loc, i) => (
                      <div key={loc.id || i} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-red-500" />
                          <span className="text-sm font-medium">{loc.suburb}, {loc.state}</span>
                          {loc.is_primary && <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Primary</span>}
                        </div>
                        <button
                          onClick={async () => {
                            try {
                              await axios.delete(`${API}/users/${carrierEmail}/locations/${loc.id}`);
                              toast.success('Location removed');
                              fetchCarrierData();
                            } catch (err) {
                              toast.error('Failed to remove location');
                            }
                          }}
                          className="text-red-500 hover:text-red-700 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-amber-700 bg-amber-50 p-3 rounded mb-4">No base locations set yet. Add one below to start receiving load alerts.</p>
                )}
                
                {(!carrierProfile?.base_locations || carrierProfile.base_locations.length < 12) && (
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    const suburb = e.target.new_suburb.value.trim();
                    const state = e.target.new_state.value;
                    if (!suburb) return;
                    try {
                      await axios.post(`${API}/users/${carrierEmail}/locations`, {
                        suburb, state,
                        is_primary: !carrierProfile?.base_locations?.length
                      });
                      toast.success(`${suburb}, ${state} added`);
                      e.target.reset();
                      fetchCarrierData();
                    } catch (err) {
                      toast.error('Failed to add location');
                    }
                  }} className="flex gap-2 items-end flex-wrap">
                    <div className="flex-1 min-w-[140px]">
                      <Label className="text-xs">Suburb/Town</Label>
                      <Input name="new_suburb" placeholder="e.g. Parramatta" required />
                    </div>
                    <div className="w-24">
                      <Label className="text-xs">State</Label>
                      <select name="new_state" defaultValue="NSW" className="w-full h-10 border border-gray-300 rounded-md px-2 text-sm">
                        {['NSW','VIC','QLD','WA','SA','TAS','NT','ACT'].map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <Button type="submit" className="bg-[#1e3a5f] hover:bg-[#2d5a8f] text-white text-sm">
                      Add
                    </Button>
                  </form>
                )}
                <p className="text-xs text-gray-500 mt-2">{carrierProfile?.base_locations?.length || 0}/12 locations</p>
              </div>

              {/* My Subscription Link */}
              <div className="max-w-md mb-8 pb-8 border-b border-gray-200">

              {/* Bank Details Section */}
              </div>
              <div className="mb-8 pb-8 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Bank Details</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Add your bank details so we can transfer load payments to you. These details are only visible to you and the admin team.
                </p>
                <form data-testid="bank-details-form" onSubmit={async (e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  try {
                    await axios.post(`${API}/users/${carrierEmail}/bank-details`, {
                      bank_account_name: formData.get('bank_account_name'),
                      bank_bsb: formData.get('bank_bsb'),
                      bank_account_number: formData.get('bank_account_number')
                    });
                    toast.success('Bank details saved');
                    fetchCarrierData();
                  } catch (err) {
                    toast.error('Failed to save bank details');
                  }
                }} className="space-y-3 max-w-md">
                  <div>
                    <Label className="text-sm">Account Name</Label>
                    <Input
                      name="bank_account_name"
                      data-testid="bank-account-name-input"
                      placeholder="e.g. Smith Transport Pty Ltd"
                      defaultValue={carrierProfile?.bank_account_name || ''}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">BSB</Label>
                      <Input
                        name="bank_bsb"
                        data-testid="bank-bsb-input"
                        placeholder="XXX-XXX"
                        maxLength={7}
                        defaultValue={carrierProfile?.bank_bsb || ''}
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Account Number</Label>
                      <Input
                        name="bank_account_number"
                        data-testid="bank-account-number-input"
                        placeholder="XXXXXXXX"
                        defaultValue={carrierProfile?.bank_account_number || ''}
                      />
                    </div>
                  </div>
                  <Button type="submit" data-testid="save-bank-details-btn" className="bg-[#1e3a5f] hover:bg-[#2d5a8f] text-white text-sm">
                    Save Bank Details
                  </Button>
                </form>
              </div>

              {/* Subscription Management */}
              <div className="max-w-md mb-8 pb-8 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Subscription Management</h3>
                <p className="text-sm text-gray-600 mb-4">
                  View your subscription details, manage billing, or cancel/pause your subscription.
                </p>
                <Button 
                  onClick={() => navigate(`/my-subscription?email=${carrierEmail}`)}
                  className="bg-[#1e3a5f] hover:bg-[#2d5a8f] text-white"
                >
                  Manage My Subscription
                </Button>
              </div>
              
              {/* Change Password Section */}
              <div className="max-w-md">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Change Password</h3>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  const currentPassword = e.target.current_password.value;
                  const newPassword = e.target.new_password.value;
                  const confirmPassword = e.target.confirm_password.value;

                  if (newPassword !== confirmPassword) {
                    alert('New passwords do not match');
                    return;
                  }

                  if (newPassword.length < 6) {
                    alert('Password must be at least 6 characters');
                    return;
                  }

                  try {
                    await axios.post(`${API}/users/change-password`, null, {
                      params: {
                        email: carrierEmail,
                        current_password: currentPassword,
                        new_password: newPassword
                      }
                    });
                    
                    alert('Password changed successfully!');
                    e.target.reset();
                  } catch (error) {
                    if (error.response?.status === 401) {
                      alert('Current password is incorrect');
                    } else {
                      alert('Failed to change password. Please try again.');
                    }
                  }
                }} className="space-y-4">
                  <div>
                    <Label htmlFor="current_password">Current Password</Label>
                    <Input
                      id="current_password"
                      name="current_password"
                      type="password"
                      required
                      placeholder="Enter current password"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="new_password">New Password</Label>
                    <Input
                      id="new_password"
                      name="new_password"
                      type="password"
                      required
                      placeholder="Enter new password (min 6 characters)"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="confirm_password">Confirm New Password</Label>
                    <Input
                      id="confirm_password"
                      name="confirm_password"
                      type="password"
                      required
                      placeholder="Confirm new password"
                    />
                  </div>

                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                    Change Password
                  </Button>
                </form>
              </div>

              {/* Apply Promo Code Section */}
              <div className="max-w-md mt-8 pt-8 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Apply Promo Code</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Have a promotional code? Apply it here to extend your subscription with free access months.
                </p>
                
                {/* Show current subscription info */}
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm font-semibold text-blue-900">Current Subscription:</p>
                  {subscriptionInfo.subscription_plan && (
                    <p className="text-sm text-blue-800">
                      Plan: <strong className="capitalize">{subscriptionInfo.subscription_plan}</strong>
                    </p>
                  )}
                  {subscriptionInfo.subscription_expiry && (
                    <p className="text-sm text-blue-800">
                      Expires: <strong>{formatDateAU(subscriptionInfo.subscription_expiry)}</strong>
                    </p>
                  )}
                  {!subscriptionInfo.is_active && subscriptionInfo.payment_completed && (
                    <p className="text-sm text-yellow-800 mt-1">⏳ Pending admin approval</p>
                  )}
                  {!subscriptionInfo.is_active && !subscriptionInfo.payment_completed && (
                    <p className="text-sm text-red-800 mt-1">⚠️ Payment required</p>
                  )}
                </div>

                {/* Show applied promo codes history if any */}
                {subscriptionInfo.promo_codes_applied && subscriptionInfo.promo_codes_applied.length > 0 && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm font-semibold text-green-900 mb-2">Previously Applied Codes:</p>
                    {subscriptionInfo.promo_codes_applied.map((promo, idx) => (
                      <div key={idx} className="text-xs text-green-800">
                        <strong>{promo.code}</strong> - {promo.extension_months} month(s) 
                        {promo.applied_at && ` on ${formatDateAU(promo.applied_at)}`}
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="Enter promo code"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    className="flex-1"
                  />
                  <Button 
                    onClick={handleApplyPromoCode}
                    disabled={applyingPromo || !promoCode.trim()}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    {applyingPromo ? 'Applying...' : 'Apply'}
                  </Button>
                </div>
                
                <p className="text-xs text-gray-500 mt-2">
                  * Promo codes extend your subscription and can be used to reactivate expired subscriptions
                </p>
              </div>
            </Card>
          )}

          {activeTab === 'compliance' && (
            <Card className="p-6">
              <h2 className="text-xl font-bold text-[#1e3a5f] mb-6">Compliance Resources</h2>

              {/* National Network Map */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-800 mb-3 border-b pb-2">National Network Map</h3>
                <a
                  href="https://maps.nhvr.gov.au/?view=Category"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 hover:underline font-medium"
                  data-testid="compliance-national-map"
                >
                  NHVR National Network Map →
                </a>
              </div>

              {/* State Oversize Networks */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">National Oversize Networks by State</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  {[
                    { state: 'SA', label: 'South Australia', url: 'https://dpti.geohub.sa.gov.au/portal/apps/webappviewer/index.html?id=48936b1308f0435382ad04387c905393' },
                    { state: 'VIC', label: 'Victoria', url: 'https://maps.nhvr.gov.au/?networkLayerContext=NATIONAL_MAP&exemptionSetId=-6&networkIds=%5B243%5D&view=Category' },
                    { state: 'QLD', label: 'Queensland', url: 'https://www.legislation.gov.au/C2025G00229/latest/text' },
                    { state: 'NSW', label: 'New South Wales', url: 'https://maps.nhvr.gov.au/?view=Category&exemptionSetId=-2&networkIds=%5B5958%5D' },
                    { state: 'WA', label: 'Western Australia', url: 'https://www.mainroads.wa.gov.au/heavy-vehicles/permit-order-scheme/osom-class-1-rav-period-permits/' },
                    { state: 'NT', label: 'Northern Territory', url: 'https://nt.gov.au/driving/heavy/oversize-or-overmass-vehicles/recommended-routes-for-oversize-loads' }
                  ].map(({ state, label, url }) => (
                    <a
                      key={state}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-4 bg-gray-50 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors"
                      data-testid={`compliance-link-${state.toLowerCase()}`}
                    >
                      <span className="w-10 h-10 bg-[#1e3a5f] text-white rounded-lg flex items-center justify-center font-bold text-sm flex-shrink-0">{state}</span>
                      <div>
                        <p className="font-medium text-gray-900">{label}</p>
                        <p className="text-xs text-blue-600">View oversize network →</p>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            </Card>
          )}

          {activeTab === 'availability' && (
            <AvailabilityTab carrierEmail={carrierEmail} />
          )}

          {activeTab === 'history' && (
            <div className="space-y-4">
              {historyLoads.length === 0 ? (
                <Card className="p-12 text-center">
                  <CheckCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-600 mb-2">No Completed Loads</h3>
                  <p className="text-gray-500">Loads you complete will appear here for your records.</p>
                </Card>
              ) : (
                historyLoads.map(load => (
                  <Card key={load.id} className="p-5" data-testid={`carrier-history-${load.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-bold text-[#1e3a5f]">{load.pickup_location} → {load.dropoff_location}</h3>
                        <p className="text-sm text-gray-500">{load.cargo_type}</p>
                      </div>
                      <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                        load.status === 'delivered' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {load.status === 'delivered' ? 'Completed' : load.status?.toUpperCase()}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm text-gray-600">
                      {load.payment_amount && (
                        <div><span className="font-medium">Amount:</span> ${load.payment_amount?.toLocaleString()}</div>
                      )}
                      {load.quote_amount && !load.payment_amount && (
                        <div><span className="font-medium">Quote:</span> ${load.quote_amount?.toLocaleString()}</div>
                      )}
                      {load.pickup_date && (
                        <div><span className="font-medium">Available From:</span> {formatDateAU(load.pickup_date)}</div>
                      )}
                      {load.completed_at && (
                        <div><span className="font-medium">Completed:</span> {formatDateAU(load.completed_at)}</div>
                      )}
                      <div><span className="font-medium">Load:</span> <span className="font-mono text-xs">{load.id.slice(0, 8)}</span></div>
                    </div>
                    {load.shipper_contact && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm">
                        <p className="font-medium text-gray-700 mb-1">Shipper:</p>
                        <p>{load.shipper_contact.name} {load.shipper_contact.company_name ? `— ${load.shipper_contact.company_name}` : ''}</p>
                        <p className="text-gray-500">{load.shipper_contact.email} | {load.shipper_contact.phone}</p>
                      </div>
                    )}
                  </Card>
                ))
              )}
            </div>
          )}

        </div>
      </div>
      
      <ConfirmModal
        isOpen={showDeleteEquipModal}
        onClose={() => {
          setShowDeleteEquipModal(false);
          setEquipToDelete(null);
        }}
        onConfirm={confirmDeleteEquipment}
        title="Delete Equipment"
        message={equipToDelete ? `Are you sure you want to delete ${equipToDelete.registration_number}? This action cannot be undone.` : ''}
        confirmText="Delete Equipment"
        variant="danger"
        loading={loading}
      />

      <ConfirmModal
        isOpen={showDeleteDocModal}
        onClose={() => {
          setShowDeleteDocModal(false);
          setDocToDelete(null);
        }}
        onConfirm={confirmDeleteDocument}
        title="Delete Document"
        message="Are you sure you want to delete this document? This action cannot be undone."
        confirmText="Delete Document"
        variant="danger"
        loading={loading}
      />

      {/* Rescind Quote Modal */}
      {showRescindModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-[#1e3a5f] mb-4">Rescind Quote</h2>
            
            <p className="text-gray-600 mb-4">
              Are you sure you want to rescind this quote? This action will withdraw your offer 
              and notify the shipper.
            </p>
            
            {quoteToRescind && (
              <div className="bg-gray-50 p-3 rounded mb-4">
                <p className="text-sm"><strong>Load:</strong> {quoteToRescind.load_details?.cargo_type}</p>
                <p className="text-sm"><strong>Quote Amount:</strong> ${quoteToRescind.amount}</p>
                <p className="text-sm"><strong>Status:</strong> {quoteToRescind.status}</p>
              </div>
            )}
            
            <div className="mb-4">
              <Label htmlFor="rescind-reason" className="text-gray-700 mb-2 block">
                Reason for rescinding <span className="text-red-500">*</span>
              </Label>
              <select
                className="w-full h-10 px-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 mb-2"
                onChange={(e) => setRescindReason(e.target.value)}
                value={rescindReason}
                data-testid="rescind-reason-select"
              >
                <option value="">Select a reason...</option>
                <option value="Circumstances have changed">Circumstances have changed</option>
                <option value="Equipment breakdown">Equipment breakdown</option>
                <option value="Driver unavailability">Driver unavailability</option>
                <option value="Schedule conflict">Schedule conflict</option>
                <option value="Pricing error">Pricing error</option>
                <option value="Other">Other (please specify below)</option>
              </select>
              
              {rescindReason === 'Other' && (
                <Input
                  id="rescind-reason-other"
                  placeholder="Please specify the reason..."
                  onChange={(e) => setRescindReason(e.target.value)}
                  className="mt-2"
                  data-testid="rescind-reason-other"
                />
              )}
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={handleRescindQuote}
                disabled={rescindingQuote || !rescindReason.trim()}
                className="flex-1 bg-orange-600 hover:bg-orange-700"
                data-testid="confirm-rescind-btn"
              >
                {rescindingQuote ? 'Rescinding...' : 'Rescind Quote'}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowRescindModal(false);
                  setQuoteToRescind(null);
                  setRescindReason('');
                }}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Hire Listing Edit Modal */}
      {hireEditTarget && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={(e) => { if (e.target === e.currentTarget && !hireEditSaving) setHireEditTarget(null); }}
          data-testid="edit-hire-overlay"
        >
          <Card className="w-full max-w-lg bg-white max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b bg-[#1e3a5f] text-white rounded-t-lg">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Edit2 className="w-5 h-5" /> Edit Hire Listing
              </h2>
              <p className="text-xs text-slate-200 mt-1">
                Update your rates, base location or quick notes. For spec changes, delete and re-list.
              </p>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <Label htmlFor="hire-edit-location">Base Location</Label>
                <Input
                  id="hire-edit-location"
                  value={hireEditForm.base_location}
                  onChange={(e) => setHireEditForm({ ...hireEditForm, base_location: e.target.value })}
                  placeholder="e.g. Parramatta, NSW"
                  className="mt-1"
                  data-testid="hire-edit-location"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label htmlFor="hire-edit-daily">Daily Rate ($)</Label>
                  <Input
                    id="hire-edit-daily"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    value={hireEditForm.indicative_daily_rate}
                    onChange={(e) => setHireEditForm({ ...hireEditForm, indicative_daily_rate: e.target.value })}
                    className="mt-1"
                    data-testid="hire-edit-daily"
                  />
                </div>
                <div>
                  <Label htmlFor="hire-edit-weekly">Weekly Rate ($)</Label>
                  <Input
                    id="hire-edit-weekly"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    value={hireEditForm.indicative_weekly_rate}
                    onChange={(e) => setHireEditForm({ ...hireEditForm, indicative_weekly_rate: e.target.value })}
                    className="mt-1"
                    data-testid="hire-edit-weekly"
                  />
                </div>
                <div>
                  <Label htmlFor="hire-edit-monthly">Monthly Rate ($)</Label>
                  <Input
                    id="hire-edit-monthly"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    value={hireEditForm.indicative_monthly_rate}
                    onChange={(e) => setHireEditForm({ ...hireEditForm, indicative_monthly_rate: e.target.value })}
                    className="mt-1"
                    data-testid="hire-edit-monthly"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="hire-edit-notes">Notes</Label>
                <textarea
                  id="hire-edit-notes"
                  value={hireEditForm.notes}
                  onChange={(e) => setHireEditForm({ ...hireEditForm, notes: e.target.value })}
                  placeholder="Anything hirers should know (restrictions, delivery options, etc.)"
                  rows={3}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1e3a5f]"
                  data-testid="hire-edit-notes"
                />
              </div>
              <div>
                <Label htmlFor="hire-edit-status">Status</Label>
                <select
                  id="hire-edit-status"
                  value={hireEditForm.status}
                  onChange={(e) => setHireEditForm({ ...hireEditForm, status: e.target.value })}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#1e3a5f]"
                  data-testid="hire-edit-status"
                >
                  <option value="active">Active — visible on marketplace</option>
                  <option value="paused">Paused — hidden, keep for later</option>
                </select>
              </div>
            </div>
            <div className="p-5 border-t flex justify-end gap-2 bg-gray-50 rounded-b-lg">
              <Button variant="outline" onClick={() => setHireEditTarget(null)} disabled={hireEditSaving} data-testid="hire-edit-cancel">
                Cancel
              </Button>
              <Button
                onClick={saveHireEdit}
                disabled={hireEditSaving}
                className="bg-[#1e3a5f] hover:bg-[#15304f] text-white"
                data-testid="hire-edit-save"
              >
                {hireEditSaving ? 'Saving…' : 'Save Changes'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Hire Listing Delete Confirm */}
      {hireDeleteTarget && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={(e) => { if (e.target === e.currentTarget && !hireDeleting) setHireDeleteTarget(null); }}
          data-testid="delete-hire-overlay"
        >
          <Card className="w-full max-w-md bg-white">
            <div className="p-5">
              <h3 className="text-lg font-semibold text-[#1e3a5f] mb-2">Remove this listing?</h3>
              <p className="text-sm text-gray-600 mb-4">
                Hirers will no longer see this listing on the Truck &amp; Trailer Hire marketplace. This cannot be undone.
              </p>
              <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-sm">
                <p className="font-semibold text-[#1e3a5f]">
                  {hireDeleteTarget.vehicle_category === 'truck'
                    ? (hireDeleteTarget.truck_type === 'Other' ? hireDeleteTarget.truck_type_other : hireDeleteTarget.truck_type) || 'Truck'
                    : hireDeleteTarget.trailer_type || 'Trailer'}
                </p>
                <p className="text-xs text-gray-500">{hireDeleteTarget.base_location}</p>
              </div>
            </div>
            <div className="p-5 border-t flex justify-end gap-2 bg-gray-50">
              <Button variant="outline" onClick={() => setHireDeleteTarget(null)} disabled={hireDeleting} data-testid="hire-delete-cancel">
                Cancel
              </Button>
              <Button
                onClick={confirmHireDelete}
                disabled={hireDeleting}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="hire-delete-confirm"
              >
                {hireDeleting ? 'Removing…' : 'Delete Listing'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default CarrierDashboard;
